import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { acBase, Spinner, fmtDateTime, fmtDate, fmtTime, buildZoomLink, ADMIN_WA } from './academyUtils';
import { T, Card, Btn, MonoLabel, Alert, Field } from '../ui';
import { hashPassword } from '../../utils/auth';
import PayrollAdmin from './PayrollAdmin';
import ProgressDashboard from '../ProgressDashboard';

const ZOOM_ACCOUNTS_IDS = [
  'a1000000-0000-0000-0000-000000000001',
  'a1000000-0000-0000-0000-000000000002',
  'a1000000-0000-0000-0000-000000000003',
];

const DEFAULT_REG_COURSES = ['IELTS','General English','German','SAT','Interview & Demo Preparation','TEFL','Other'];

// ── Tiny reusable components ────────────────────────────────────────────────
function SectionHeader({ title, action }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a' }}>{title}</div>
      {action}
    </div>
  );
}

function StatusDot({ active }) {
  return <span style={{ display:'inline-block', width:8, height:8, borderRadius:'50%', background:active?'#27ae60':'#9aa3b8', marginRight:6 }}/>;
}

// ── Main component ──────────────────────────────────────────────────────────
const SCORE_STEPS = [0,0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9];

export default function AcademyAdmin() {
  const [subTab,      setSubTab]      = useState('teachers');
  const [teachers,    setTeachers]    = useState([]);
  const [students,    setStudents]    = useState([]);
  const [bookings,    setBookings]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [zoomAccounts,setZoomAccounts]= useState([]);
  const [speakingExams,setSpeakingExams]= useState([]);
  const [spFilter,    setSpFilter]   = useState({ examiner:'all', status:'all' });
  const [addingExam,  setAddingExam]  = useState(false);
  const [newExam,     setNewExam]     = useState({ student_name:'', student_whatsapp:'', examiner_id:'', scheduled_at:'', booking_type:'package', notes:'' });
  const [loading,     setLoading]     = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [editCat,     setEditCat]     = useState(null);
  const [addingCat,   setAddingCat]   = useState(false);
  const [newCat,      setNewCat]      = useState({ name:'', icon:'📚', color:'#1a2a4a', bg_color:'#e8ecf5' });
  const [saving,      setSaving]      = useState(false);
  const [msg,         setMsg]         = useState('');

  // Teacher edit form
  const [tName,       setTName]       = useState('');
  const [tPin,        setTPin]        = useState('');
  const [tZoomId,     setTZoomId]     = useState('');
  const [tZoomAc,     setTZoomAc]     = useState('');
  const [tWA,         setTWA]         = useState('');
  const [tEmail,      setTEmail]      = useState('');
  const [tTempPass,   setTTempPass]   = useState('');
  const [tActive,     setTActive]     = useState(true);
  const [tCats,       setTCats]       = useState([]);
  const [tBio,        setTBio]        = useState('');
  const [tNat,        setTNat]        = useState('');
  const [tYearsExp,   setTYearsExp]   = useState(0);
  const [tMaxStudents,setTMaxStudents]= useState(10);
  const [tVisPublic,  setTVisPublic]  = useState(false);
  const [tNative,     setTNative]     = useState(false);

  // Adding new teacher
  const [addingTeacher, setAddingTeacher] = useState(false);

  // Student panel
  const [expandedStudent, setExpandedStudent]  = useState(null); // student id
  const [studentSubs,     setStudentSubs]      = useState({});   // { student_id: [subs] }
  const [assignForm,      setAssignForm]       = useState({ teacher_id:'', subscription_type:'general-24h', total_hours:24, start_date:'', notes:'' });

  // Student subscription form (legacy, kept for backward compat)
  const [subForm, setSubForm] = useState({ subscription_type:'standard', total_hours:24, start_date:'', notes:'' });

  // Booking filter
  const [filterTeacher, setFilterTeacher] = useState('all');

  // Cancellation modal
  const [cancelModal, setCancelModal]     = useState(null); // booking object
  const [cancelWho,   setCancelWho]       = useState('');   // 'teacher' | 'student'
  const [cancelNotice,setCancelNotice]    = useState(48);
  const [cancelCount, setCancelCount]     = useState(1);
  const [cancelSaving,setCancelSaving]    = useState(false);

  // Zoom CRUD
  const [editZoom,    setEditZoom]    = useState(null);   // { id, account_name, max_simultaneous }
  const [addingZoom,  setAddingZoom]  = useState(false);
  const [newZoom,     setNewZoom]     = useState({ account_name:'', max_simultaneous:2 });
  const [zoomSaving,  setZoomSaving]  = useState(false);

  // Teacher profile extras
  const [tPhotoUrl,   setTPhotoUrl]   = useState('');
  const [tVoiceUrl,   setTVoiceUrl]   = useState('');
  const [tSamples,    setTSamples]    = useState([]);

  // Add student
  const [addingStudent, setAddingStudent] = useState(false);
  const [newStudent,    setNewStudent]    = useState({ name:'', email:'', whatsapp:'', pin:'', password:'', subscription_type:'general_english', total_hours:24, notes:'' });
  const [studentSaving, setStudentSaving] = useState(false);

  // Edit student
  const [editingStudent,   setEditingStudent]   = useState(null);
  const [editStudentForm,  setEditStudentForm]  = useState({});
  const [editStudentSaving,setEditStudentSaving]= useState(false);

  // Teacher filters
  const [teacherSearch,       setTeacherSearch]       = useState('');
  const [teacherFilterCat,    setTeacherFilterCat]    = useState('all');
  const [teacherFilterStatus, setTeacherFilterStatus] = useState('all');

  // ── Subscription tiers (FIX 14) ──────────────────────────────────────────
  const [subTiers,      setSubTiers]      = useState([]);
  const [editingTier,   setEditingTier]   = useState(null);
  const [tierForm,      setTierForm]      = useState({ name:'', name_ar:'', price:'', currency:'AED', duration_value:'1', duration_unit:'months', tier_type:'full_course', hours_included:'', features:'', is_active:true, show_price:true, color:'#1a2a4a', sort_order:'0', course_id:'', whatsapp_message:'', writing_exams_included:'', speaking_exams_included:'', grace_period_days:'60' });
  const [tierSaving,    setTierSaving]    = useState(false);
  const [tierMsg,       setTierMsg]       = useState('');

  // ── Branding / settings (FIX 15) ─────────────────────────────────────────
  const [brandSettings, setBrandSettings] = useState({});
  const [brandForm,     setBrandForm]     = useState({ academy_name:'Compass Academy', tagline:'Your path to IELTS success', primary_color:'#1a2a4a', whatsapp_number:'201008182425', whatsapp_number_2:'', logo_url:'', favicon_url:'', hero_stat_1_number:'Band 7.0+', hero_stat_1_label:'Average Score', hero_stat_2_number:'Egypt & Gulf', hero_stat_2_label:'Region', hero_stat_3_number:'18+', hero_stat_3_label:'Expert Trainers', hero_stat_4_number:'4–5 Weeks', hero_stat_4_label:'To Band 7+' });
  const [brandSaving,   setBrandSaving]   = useState(false);
  const [brandMsg,      setBrandMsg]      = useState('');
  const [regCourses,     setRegCourses]     = useState(DEFAULT_REG_COURSES);
  const [regCourseInput, setRegCourseInput] = useState('');
  const [regCourseSaving,setRegCourseSaving]= useState(false);
  const [regCourseMsg,   setRegCourseMsg]   = useState('');

  // ── Results & testimonials (FIX 17) ──────────────────────────────────────
  const [results,        setResults]       = useState([]);
  const [testimonials,   setTestimonials]  = useState([]);
  const [enquiries,      setEnquiries]     = useState([]);
  const [editingResult,  setEditingResult] = useState(null);
  const [editingTesti,   setEditingTesti]  = useState(null);
  const [resultForm,     setResultForm]    = useState({ student_name:'', country:'Egypt', before_score:'', after_score:'', comment:'', screenshot_url:'', is_featured:false, sort_order:'0' });
  const [testiForm,      setTestiForm]     = useState({ student_name:'', country:'Egypt', rating:5, text:'', is_featured:false });
  const [resultSaving,   setResultSaving]  = useState(false);
  const [resultMsg,      setResultMsg]     = useState('');
  const [resultUploading,setResultUploading] = useState(false);
  const [testiSaving,    setTestiSaving]   = useState(false);
  const [resultsSubTab,  setResultsSubTab] = useState('results');

  // ── Reports (FIX 19) ─────────────────────────────────────────────────────
  const [reportRange,    setReportRange]   = useState('month');

  // ── Courses manager ───────────────────────────────────────────────────────
  const [adminCourses,   setAdminCourses]  = useState([]);
  const [editingCourse,  setEditingCourse] = useState(null);
  const [courseForm,     setCourseForm]    = useState({ name:'', name_ar:'', cefr_level:'', description:'', description_ar:'', icon:'📚', color:'#1a2a4a', bg_color:'#f0f4f8', border_color:'#dde3ed', active:true, sort_order:'0' });
  const [courseSaving,   setCourseSaving]  = useState(false);
  const [courseMsg,      setCourseMsg]     = useState('');

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('teachers')
      .select('*, zoom_accounts(account_name), teacher_categories_map(teacher_categories(id,name,icon,color))')
      .order('name');
    setTeachers(data || []);
    setLoading(false);
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('students')
      .select('*, subscriptions(id,subscription_type,total_hours,used_hours,status)')
      .order('name');
    setStudents(data || []);
    setLoading(false);
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('bookings')
      .select('*, teachers(name,zoom_account_id), students(name,whatsapp), time_slots(start_time,end_time), zoom_accounts(account_name)')
      .order('created_at', { ascending:false })
      .limit(100);
    setBookings(data || []);
    setLoading(false);
  }, []);

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase.from('teacher_categories').select('*').order('sort_order');
    setCategories(data || []);
  }, []);

  const fetchZoom = useCallback(async () => {
    const { data } = await supabase.from('zoom_accounts').select('*').order('account_name');
    setZoomAccounts(data || []);
  }, []);

  const fetchSpeakingExams = useCallback(async () => {
    try {
      const { data } = await supabase.from('speaking_exams')
        .select('*, teachers(name)')
        .order('scheduled_at', { ascending: false });
      setSpeakingExams(data || []);
    } catch { setSpeakingExams([]); }
  }, []);

  const fetchTiers = useCallback(async () => {
    const { data } = await supabase.from('subscription_tiers').select('*').order('sort_order');
    setSubTiers(data || []);
  }, []);

  const fetchAdminCourses = useCallback(async () => {
    const { data } = await supabase.from('courses').select('*').order('sort_order');
    setAdminCourses(data || []);
  }, []);

  const fetchBranding = useCallback(async () => {
    const { data } = await supabase.from('academy_settings').select('*');
    const map = {};
    (data || []).forEach(r => { map[r.setting_key] = r.setting_value; });
    setBrandSettings(map);
    setBrandForm(prev => ({
      academy_name:       map.academy_name       || prev.academy_name,
      tagline:            map.tagline            || prev.tagline,
      primary_color:      map.primary_color      || prev.primary_color,
      whatsapp_number:    map.whatsapp_number    || prev.whatsapp_number,
      whatsapp_number_2:  map.whatsapp_number_2  || '',
      logo_url:           map.logo_url           || '',
      favicon_url:        map.favicon_url        || '',
      hero_stat_1_number: map.hero_stat_1_number || prev.hero_stat_1_number,
      hero_stat_1_label:  map.hero_stat_1_label  || prev.hero_stat_1_label,
      hero_stat_2_number: map.hero_stat_2_number || prev.hero_stat_2_number,
      hero_stat_2_label:  map.hero_stat_2_label  || prev.hero_stat_2_label,
      hero_stat_3_number: map.hero_stat_3_number || prev.hero_stat_3_number,
      hero_stat_3_label:  map.hero_stat_3_label  || prev.hero_stat_3_label,
      hero_stat_4_number: map.hero_stat_4_number || prev.hero_stat_4_number,
      hero_stat_4_label:  map.hero_stat_4_label  || prev.hero_stat_4_label,
    }));
    try {
      const parsed = map.registration_courses ? JSON.parse(map.registration_courses) : null;
      setRegCourses(Array.isArray(parsed) ? parsed : DEFAULT_REG_COURSES);
    } catch { setRegCourses(DEFAULT_REG_COURSES); }
  }, []);

  const fetchResults = useCallback(async () => {
    const [r, t, e] = await Promise.all([
      supabase.from('student_results').select('*').order('sort_order'),
      supabase.from('testimonials').select('*').order('created_at', { ascending:false }),
      supabase.from('enquiries').select('*').order('created_at', { ascending:false }).limit(100),
    ]);
    setResults(r.data || []);
    setTestimonials(t.data || []);
    setEnquiries(e.data || []);
  }, []);

  // Always keep categories and zoom accounts loaded — needed by the teacher edit form
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchZoom(); },       [fetchZoom]);

  useEffect(() => {
    if (subTab === 'teachers')   fetchTeachers();
    if (subTab === 'students')   fetchStudents();
    if (subTab === 'bookings')   { fetchBookings(); fetchTeachers(); }
    if (subTab === 'zoom')       { fetchZoom(); fetchTeachers(); fetchBookings(); }
    if (subTab === 'speaking')   { fetchSpeakingExams(); fetchTeachers(); }
    if (subTab === 'reminders')  fetchStudents();
    if (subTab === 'tiers')      { fetchTiers(); fetchAdminCourses(); }
    if (subTab === 'branding')   fetchBranding();
    if (subTab === 'results')    fetchResults();
    if (subTab === 'reports')    { fetchTeachers(); fetchBookings(); fetchStudents(); }
    if (subTab === 'courses')    fetchAdminCourses();
  }, [subTab, fetchTeachers, fetchStudents, fetchBookings, fetchZoom, fetchSpeakingExams, fetchTiers, fetchBranding, fetchResults, fetchAdminCourses]);

  function openEditTeacher(t) {
    setEditTeacher(t);
    setTName(t.name); setTPin(''); setTZoomId(t.zoom_meeting_id||'');
    setTZoomAc(t.zoom_account_id||''); setTWA(t.whatsapp||''); setTEmail(t.email||''); setTTempPass(''); setTActive(t.active);
    setTCats(t.teacher_categories_map?.map(m=>m.teacher_categories?.id).filter(Boolean) || []);
    setTBio(t.bio||''); setTNat(t.nationality||''); setTYearsExp(t.years_experience||0);
    setTMaxStudents(t.max_students||10); setTVisPublic(t.visible_to_public||false); setTNative(t.native_speaker||false);
    setTPhotoUrl(t.photo_url||''); setTVoiceUrl(t.voice_intro_url||'');
    setTSamples(Array.isArray(t.sample_work) ? t.sample_work : []);
    setMsg('');
  }

  function openAddTeacher() {
    setEditTeacher({ id:null });
    setTName(''); setTPin('1000'); setTZoomId(''); setTZoomAc(ZOOM_ACCOUNTS_IDS[0]); setTWA(''); setTEmail(''); setTTempPass(''); setTActive(true); setTCats([]);
    setTBio(''); setTNat(''); setTYearsExp(0); setTMaxStudents(10); setTVisPublic(false); setTNative(false);
    setTPhotoUrl(''); setTVoiceUrl(''); setTSamples([]);
    setAddingTeacher(true); setMsg('');
  }

  async function saveTeacher() {
    setSaving(true); setMsg('');
    try {
      const upd = {
        name: tName.trim(), zoom_meeting_id: tZoomId.trim(), zoom_account_id: tZoomAc||null,
        whatsapp: tWA.trim(), email: tEmail.trim().toLowerCase()||null, active: tActive,
        bio: tBio.trim(), nationality: tNat.trim(), years_experience: Number(tYearsExp)||0,
        max_students: Number(tMaxStudents)||10, visible_to_public: tVisPublic, native_speaker: tNative,
        photo_url: tPhotoUrl.trim(), voice_intro_url: tVoiceUrl.trim(),
        sample_work: tSamples,
      };
      if (tPin) upd.pin = tPin;
      if (tTempPass.trim()) upd.password_hash = await hashPassword(tTempPass.trim());

      let teacherId = editTeacher.id;
      if (!teacherId) {
        const { data, error } = await supabase.from('teachers').insert({ ...upd, pin:tPin||'0000', approved:true }).select('id').single();
        if (error) throw error;
        teacherId = data.id;
      } else {
        const { error } = await supabase.from('teachers').update(upd).eq('id', teacherId);
        if (error) throw error;
      }

      // Update categories
      await supabase.from('teacher_categories_map').delete().eq('teacher_id', teacherId);
      if (tCats.length > 0) {
        await supabase.from('teacher_categories_map').insert(tCats.map(cid => ({ teacher_id:teacherId, category_id:cid })));
      }

      setEditTeacher(null); setAddingTeacher(false);
      fetchTeachers();
      setMsg('✓ Teacher updated successfully');
      setTimeout(() => setMsg(''), 3500);
    } catch (e) { setMsg('Error saving. Try again.'); }
    setSaving(false);
  }

  async function fetchStudentSubs(studentId) {
    const { data } = await supabase.from('subscriptions')
      .select('*, teachers(id,name)')
      .eq('student_id', studentId)
      .eq('status', 'active')
      .order('created_at');
    setStudentSubs(prev => ({ ...prev, [studentId]: data || [] }));
  }

  async function assignTeacher(studentId) {
    if (!assignForm.teacher_id) return;
    setSaving(true);
    const hoursMap = { 'general-24h':24, 'ielts-standard':48, 'ielts-premium':9999, 'custom': Number(assignForm.total_hours)||24 };
    const { error } = await supabase.from('subscriptions').insert({
      student_id: studentId,
      teacher_id: assignForm.teacher_id,
      subscription_type: assignForm.subscription_type,
      total_hours: hoursMap[assignForm.subscription_type] ?? Number(assignForm.total_hours) ?? 24,
      used_hours: 0,
      start_date: assignForm.start_date || new Date().toISOString().slice(0,10),
      notes: assignForm.notes,
      status: 'active',
    });
    setSaving(false);
    if (error) { alert('Failed to assign teacher: ' + error.message); return; }
    setAssignForm({ teacher_id:'', subscription_type:'general-24h', total_hours:24, start_date:'', notes:'' });
    fetchStudentSubs(studentId);
    fetchStudents();
  }

  async function removeAssignment(subId, studentId) {
    const { error } = await supabase.from('subscriptions').update({ status:'inactive' }).eq('id', subId);
    if (error) { alert('Failed to remove assignment: ' + error.message); return; }
    fetchStudentSubs(studentId);
    fetchStudents();
  }

  async function updateBookingStatus(id, status) {
    await supabase.from('bookings').update({ status }).eq('id', id);
    fetchBookings();
  }

  async function confirmBooking(b) {
    await supabase.from('bookings').update({ status: 'confirmed' }).eq('id', b.id);
    if (b.slot_id) await supabase.from('time_slots').update({ status: 'booked' }).eq('id', b.slot_id);
    fetchBookings();
  }

  // Auto-log hours when booking is completed
  async function completeBooking(b) {
    await supabase.from('bookings').update({ status: 'completed' }).eq('id', b.id);

    const startISO = b.time_slots?.start_time;
    const endISO   = b.time_slots?.end_time;
    if (startISO && endISO && b.teacher_id) {
      const hrs = (new Date(endISO) - new Date(startISO)) / 3600000;

      // Log teacher hours
      await supabase.from('teacher_hours_log').insert({
        teacher_id: b.teacher_id, booking_id: b.id,
        student_id: b.student_id || null,
        session_date: startISO.slice(0, 10),
        scheduled_duration: hrs, actual_duration: hrs,
        hours_counted: hrs, session_status: 'taught',
      });

      // Deduct from student subscription used_hours
      if (b.student_id) {
        const { data: sub } = await supabase.from('subscriptions')
          .select('id,used_hours,total_hours')
          .eq('student_id', b.student_id)
          .eq('teacher_id', b.teacher_id)
          .eq('status', 'active')
          .single();
        if (sub) {
          const newUsed = Math.min(Number(sub.total_hours||0), Number(sub.used_hours||0) + hrs);
          await supabase.from('subscriptions').update({ used_hours: newUsed }).eq('id', sub.id);
        }
        // Also try updating students.used_hours directly (if column exists)
        try {
          const { data: st } = await supabase.from('students').select('used_hours').eq('id', b.student_id).single();
          if (st !== null) {
            await supabase.from('students').update({ used_hours: Number(st.used_hours||0) + hrs }).eq('id', b.student_id);
          }
        } catch {}
      }
    }
    fetchBookings();
  }

  // Open cancellation modal
  function openCancelModal(b) {
    setCancelModal(b);
    setCancelWho(''); setCancelNotice(48); setCancelCount(1);
  }

  async function processCancellation() {
    if (!cancelModal || !cancelWho) return;
    setCancelSaving(true);

    let sessionStatus, hoursCounted;
    if (cancelWho === 'teacher') {
      sessionStatus = 'cancelled_by_teacher';
      hoursCounted  = 0;
    } else {
      // Student: excused if >= 48h notice AND first cancellation this month
      const excused = cancelNotice >= 48 && cancelCount <= 1;
      sessionStatus = excused ? 'cancelled_by_student_excused' : 'cancelled_by_student_charged';
      hoursCounted  = excused ? 0 : 0.5; // teacher gets half session if student charged
    }

    await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', cancelModal.id);

    const startISO = cancelModal.time_slots?.start_time;
    if (startISO && cancelModal.teacher_id) {
      const hrs = cancelModal.time_slots?.end_time
        ? (new Date(cancelModal.time_slots.end_time) - new Date(startISO)) / 3600000
        : 1;
      await supabase.from('teacher_hours_log').insert({
        teacher_id:                      cancelModal.teacher_id,
        booking_id:                      cancelModal.id,
        student_id:                      cancelModal.student_id || null,
        session_date:                    startISO.slice(0, 10),
        scheduled_duration:              hrs,
        actual_duration:                 0,
        hours_counted:                   hoursCounted,
        session_status:                  sessionStatus,
        cancellation_notice_hours:       cancelWho === 'student' ? cancelNotice : null,
        student_month_cancellation_count: cancelWho === 'student' ? cancelCount : 0,
      });
    }

    setCancelModal(null); setCancelSaving(false);
    fetchBookings();
  }

  async function saveCategory() {
    setSaving(true);
    try {
      if (editCat?.id) {
        await supabase.from('teacher_categories').update(editCat).eq('id', editCat.id);
      } else {
        await supabase.from('teacher_categories').insert(newCat);
        setNewCat({ name:'', icon:'📚', color:'#1a2a4a', bg_color:'#e8ecf5' });
        setAddingCat(false);
      }
      setEditCat(null);
      fetchCategories();
    } catch (e) { /* silent */ }
    setSaving(false);
  }

  async function addSubscription(studentId) {
    setSaving(true);
    const { error } = await supabase.from('subscriptions').insert({
      student_id: studentId,
      ...subForm,
      total_hours: Number(subForm.total_hours),
      used_hours: 0, status:'active',
    });
    setSaving(false);
    if (error) { alert('Failed to add subscription: ' + error.message); return; }
    fetchStudents();
  }

  async function saveEditStudent() {
    if (!editStudentForm.name?.trim() || !editStudentForm.whatsapp?.trim()) {
      alert('Name and WhatsApp are required.'); return;
    }
    setEditStudentSaving(true);
    try {
      const upd = {
        name:              editStudentForm.name.trim(),
        whatsapp:          editStudentForm.whatsapp.trim(),
        email:             editStudentForm.email?.trim().toLowerCase() || null,
        subscription_type: editStudentForm.subscription_type,
        total_hours:       Number(editStudentForm.total_hours) || 24,
        notes:             editStudentForm.notes?.trim() || '',
      };
      if (editStudentForm.pin?.length === 4) upd.pin = editStudentForm.pin;
      if (editStudentForm.newPassword?.trim()) {
        upd.password_hash = await hashPassword(editStudentForm.newPassword.trim());
      }
      const { error } = await supabase.from('students').update(upd).eq('id', editingStudent.id);
      if (error) { alert('Failed to save: ' + error.message); setEditStudentSaving(false); return; }
      setEditingStudent(null);
      setEditStudentForm({});
      fetchStudents();
    } catch(e) { alert('Error: ' + e.message); }
    setEditStudentSaving(false);
  }

  async function resetStudentPin(studentId, newPin) {
    await supabase.from('students').update({ pin:newPin }).eq('id', studentId);
  }

  async function deleteStudent(id) {
    if (!window.confirm('Are you sure you want to delete this student? This cannot be undone.')) return;
    await supabase.from('students').delete().eq('id', id);
    fetchStudents();
  }

  async function addStudent() {
    if (!newStudent.name.trim() || !newStudent.whatsapp.trim()) return;
    if (!newStudent.password.trim()) { alert('Please set a password so the student can log in.'); return; }
    setStudentSaving(true);
    try {
      const hoursMap = { general_english:24, ielts_standard:48, ielts_premium:9999, custom: Number(newStudent.total_hours)||24 };
      const row = {
        name: newStudent.name.trim(),
        whatsapp: newStudent.whatsapp.trim(),
        subscription_type: newStudent.subscription_type,
        total_hours: hoursMap[newStudent.subscription_type] ?? Number(newStudent.total_hours) ?? 24,
        used_hours: 0,
        notes: newStudent.notes.trim(),
        active: true,
        password_hash: await hashPassword(newStudent.password.trim()),
      };
      if (newStudent.email.trim()) row.email = newStudent.email.trim().toLowerCase();
      if (newStudent.pin.length === 4) row.pin = newStudent.pin;
      const { data: insertData, error: insertError } = await supabase.from('students').insert(row).select();
      if (insertError) throw insertError;
      setAddingStudent(false);
      setNewStudent({ name:'', email:'', whatsapp:'', pin:'', password:'', subscription_type:'general_english', total_hours:24, notes:'' });
      fetchStudents();
    } catch(e) { /* silent */ }
    setStudentSaving(false);
  }

  // ── Zoom CRUD ──
  async function saveZoomAccount() {
    if (!newZoom.account_name.trim()) return;
    setZoomSaving(true);
    try {
      await supabase.from('zoom_accounts').insert({
        account_name: newZoom.account_name.trim(),
        max_simultaneous: Number(newZoom.max_simultaneous)||2,
      });
      setNewZoom({ account_name:'', max_simultaneous:2 });
      setAddingZoom(false);
      fetchZoom();
    } catch(e) { /* silent */ }
    setZoomSaving(false);
  }

  async function updateZoomAccount(id, fields) {
    setZoomSaving(true);
    await supabase.from('zoom_accounts').update(fields).eq('id', id);
    setEditZoom(null);
    fetchZoom();
    setZoomSaving(false);
  }

  async function deleteZoomAccount(z) {
    const assignedCount = teachers.filter(t => t.zoom_account_id === z.id).length;
    if (assignedCount > 0) {
      alert(`Cannot delete "${z.account_name}" — ${assignedCount} teacher(s) are assigned to it. Reassign them first.`);
      return;
    }
    if (!window.confirm(`Delete zoom account "${z.account_name}"? This cannot be undone.`)) return;
    await supabase.from('zoom_accounts').delete().eq('id', z.id);
    fetchZoom();
  }

  const filteredBookings = filterTeacher === 'all'
    ? bookings
    : bookings.filter(b => b.teacher_id === filterTeacher);

  const filteredTeachers = teachers.filter(t => {
    const matchName = teacherSearch === '' || t.name.toLowerCase().includes(teacherSearch.toLowerCase());
    const matchCat  = teacherFilterCat === 'all' ||
      (t.teacher_categories_map||[]).some(m => m.teacher_categories?.id === teacherFilterCat);
    const matchSt   = teacherFilterStatus === 'all' ||
      (teacherFilterStatus === 'active' ? t.active : !t.active);
    return matchName && matchCat && matchSt;
  });

  const pendingCount = bookings.filter(b => b.status === 'pending').length;

  const SUBTABS = [
    ['teachers',   '👥 Teachers'],
    ['students',   '🎓 Students'],
    ['bookings',   `📋 Bookings${pendingCount > 0 ? ` (${pendingCount} pending)` : ''}`],
    ['categories', '🏷 Teacher Categories'],
    ['zoom',       '📹 Zoom Accounts'],
    ['speaking',   '🎯 Speaking Exams'],
    ['payroll',    '💰 Payroll'],
    ['reminders',  '🔔 Reminders'],
    ['courses',    '📚 Courses'],
    ['tiers',      '💳 Subscription Tiers'],
    ['branding',   '🎨 Branding & Settings'],
    ['results',    '🏆 Results & Testimonials'],
    ['reports',    '📊 Reports'],
  ];

  return (
    <div>
      {/* Sub-tabs */}
      <div style={{ display:'flex', gap:6, overflowX:'auto', marginBottom:16, paddingBottom:4, flexWrap:'nowrap', WebkitOverflowScrolling:'touch' }}>
        {SUBTABS.map(([k,l])=>(
          <button key={k} onClick={()=>setSubTab(k)}
            style={{ padding:'8px 14px', borderRadius:7, border:`1px solid ${subTab===k?T.gold:T.border}`, background:subTab===k?T.gold+'22':'transparent', color:subTab===k?T.gold:T.muted, cursor:'pointer', fontSize:12.5, fontFamily:"'Plus Jakarta Sans',sans-serif", whiteSpace:'nowrap', flexShrink:0, minHeight:36 }}>
            {l}
          </button>
        ))}
      </div>

      {loading && <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner/></div>}

      {/* ── TEACHERS ── */}
      {!loading && subTab === 'teachers' && (
        <div>
          <SectionHeader title={`Teachers (${teachers.length})`}
            action={<Btn variant="primary" small onClick={openAddTeacher}>+ Add Teacher</Btn>}/>

          {msg && (
            <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:12, fontSize:13, fontWeight:500,
              background: msg.startsWith('✓') ? '#e8f5ee' : '#fff5f5',
              border: `1px solid ${msg.startsWith('✓') ? '#c8ead8' : 'rgba(192,57,43,0.2)'}`,
              color: msg.startsWith('✓') ? '#1a7a4a' : '#c0392b' }}>
              {msg}
            </div>
          )}

          {(editTeacher || addingTeacher) && (
            <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
              <div style={{ fontSize:14, fontWeight:600, color:T.gold, marginBottom:12 }}>
                {editTeacher?.id ? `Edit: ${editTeacher.name}` : 'New Teacher'}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <Field label="Name"><input value={tName} onChange={e=>setTName(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                </div>
                <div>
                  <Field label="PIN (4 digits)"><input value={tPin} onChange={e=>setTPin(e.target.value)} maxLength={4} placeholder="Leave blank to keep" style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                </div>
                <div>
                  <Field label="Zoom Meeting ID"><input value={tZoomId} onChange={e=>setTZoomId(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                </div>
                <div>
                  <Field label="Zoom Account">
                    <select value={tZoomAc} onChange={e=>setTZoomAc(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}>
                      <option value="">None</option>
                      {zoomAccounts.map(z=><option key={z.id} value={z.id}>{z.account_name}</option>)}
                    </select>
                  </Field>
                </div>
                <div>
                  <Field label="WhatsApp"><input value={tWA} onChange={e=>setTWA(e.target.value)} placeholder="201012345678" style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                  <Field label="Email (for login)"><input type="email" value={tEmail} onChange={e=>setTEmail(e.target.value)} placeholder="teacher@email.com" style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                  <Field label="Temporary Password (leave blank to keep existing)"><input type="password" value={tTempPass} onChange={e=>setTTempPass(e.target.value)} placeholder="Set a new password…" style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                </div>
                <div>
                  <Field label="Status">
                    <div style={{ display:'flex', gap:8 }}>
                      {[['active',true],['inactive',false]].map(([lbl,val])=>(
                        <button key={lbl} onClick={()=>setTActive(val)}
                          style={{ flex:1, padding:'8px', borderRadius:6, cursor:'pointer', border:`1.5px solid ${tActive===val?T.gold:T.border}`, background:tActive===val?T.gold+'22':'transparent', color:tActive===val?T.gold:T.muted, fontSize:12 }}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>
              <Field label="Categories" style={{ marginTop:8 }}>
                {categories.length === 0 ? (
                  <div style={{ fontSize:12, color:'#9aa3b8', fontStyle:'italic' }}>Loading categories…</div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:6 }}>
                    {categories.map(c => {
                      const on = tCats.includes(c.id);
                      return (
                        <label key={c.id} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'7px 10px', borderRadius:8, border:`1.5px solid ${on ? c.color : T.border}`, background:on ? (c.bg_color||'#f7f9fc') : 'transparent', transition:'all .12s' }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => setTCats(prev => on ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                            style={{ width:15, height:15, accentColor: c.color, cursor:'pointer', flexShrink:0 }}
                          />
                          <span style={{ fontSize:14 }}>{c.icon}</span>
                          <span style={{ fontSize:13, fontWeight: on ? 600 : 400, color: on ? c.color : T.muted }}>{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </Field>

              {/* Extended profile fields */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:4 }}>
                <Field label="Nationality">
                  <input value={tNat} onChange={e=>setTNat(e.target.value)} placeholder="e.g. Egyptian, British" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Years of Experience">
                  <input type="number" min={0} value={tYearsExp} onChange={e=>setTYearsExp(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Max Students Capacity">
                  <input type="number" min={1} value={tMaxStudents} onChange={e=>setTMaxStudents(e.target.value)} style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
              </div>

              <Field label="Bio (shown on profile)" style={{ marginTop:8 }}>
                <textarea value={tBio} onChange={e=>setTBio(e.target.value)} rows={2} placeholder="Short description of teaching style and experience..."
                  style={{ ...acBase.inp, resize:'vertical', marginBottom:0 }}/>
              </Field>

              <div style={{ display:'flex', gap:12, marginTop:8, flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:'#1a2233' }}>
                  <input type="checkbox" checked={tVisPublic} onChange={e=>setTVisPublic(e.target.checked)}/>
                  Visible to public
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13, color:'#1a2233' }}>
                  <input type="checkbox" checked={tNative} onChange={e=>setTNative(e.target.checked)}/>
                  🇬🇧 Native speaker
                </label>
              </div>

              {/* ── Photo URL ── */}
              <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #eef1f7' }}>
                <Field label="Profile Photo URL">
                  <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                    <div style={{ flex:1 }}>
                      <input value={tPhotoUrl} onChange={e=>setTPhotoUrl(e.target.value)}
                        placeholder="Paste Google Drive or direct image URL"
                        style={{ ...acBase.inp, marginBottom:4 }}/>
                      <div style={{ fontSize:11, color:'#9aa3b8' }}>Paste a Google Drive image link or any direct image URL</div>
                    </div>
                    {tPhotoUrl && (
                      <img src={tPhotoUrl} alt="preview"
                        style={{ width:52, height:52, borderRadius:8, objectFit:'cover', border:'1px solid #dde3ed', flexShrink:0 }}
                        onError={e => { e.target.style.display='none'; }}/>
                    )}
                  </div>
                </Field>

                {/* ── Voice Introduction ── */}
                <Field label="Voice Introduction URL" style={{ marginTop:8 }}>
                  <input value={tVoiceUrl} onChange={e=>setTVoiceUrl(e.target.value)}
                    placeholder="Paste Google Drive audio link or any audio URL"
                    style={{ ...acBase.inp, marginBottom:4 }}/>
                  <div style={{ fontSize:11, color:'#9aa3b8' }}>Record a 30-second introduction and paste the link here</div>
                </Field>

                {/* ── Sample Work ── */}
                <div style={{ marginTop:8 }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <MonoLabel>Sample Work / Portfolio ({tSamples.length}/5)</MonoLabel>
                    {tSamples.length < 5 && (
                      <button onClick={() => setTSamples(s => [...s, { title:'', url:'', type:'Recording' }])}
                        style={{ fontSize:12, color:T.gold, border:`1px solid ${T.gold}44`, background:T.gold+'11', borderRadius:6, padding:'3px 10px', cursor:'pointer' }}>
                        + Add Sample
                      </button>
                    )}
                  </div>
                  {tSamples.map((sm, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:6, marginBottom:6, alignItems:'center' }}>
                      <input value={sm.title} onChange={e=>setTSamples(s=>s.map((x,j)=>j===i?{...x,title:e.target.value}:x))}
                        placeholder="e.g. Sample Lesson Recording" style={{ ...acBase.inp, marginBottom:0, fontSize:12 }}/>
                      <input value={sm.url} onChange={e=>setTSamples(s=>s.map((x,j)=>j===i?{...x,url:e.target.value}:x))}
                        placeholder="Google Drive or video URL" style={{ ...acBase.inp, marginBottom:0, fontSize:12 }}/>
                      <select value={sm.type} onChange={e=>setTSamples(s=>s.map((x,j)=>j===i?{...x,type:e.target.value}:x))}
                        style={{ ...acBase.inp, marginBottom:0, fontSize:12, width:120 }}>
                        {['Recording','Presentation','Worksheet','Other'].map(v=><option key={v}>{v}</option>)}
                      </select>
                      <button onClick={()=>setTSamples(s=>s.filter((_,j)=>j!==i))}
                        style={{ background:'rgba(192,57,43,0.1)', color:'#c0392b', border:'none', borderRadius:6, padding:'6px 9px', cursor:'pointer', fontSize:13 }}>✕</button>
                    </div>
                  ))}
                  {tSamples.length === 0 && (
                    <div style={{ fontSize:12, color:'#9aa3b8', fontStyle:'italic' }}>No samples added yet. Click "+ Add Sample" to add portfolio items.</div>
                  )}
                </div>
              </div>

              <div style={{ display:'flex', gap:8, marginTop:14 }}>
                <Btn variant="primary" onClick={saveTeacher} disabled={saving}>{saving?'Saving…':'Save Teacher'}</Btn>
                <Btn onClick={()=>{ setEditTeacher(null); setAddingTeacher(false); setMsg(''); }}>Cancel</Btn>
              </div>
            </Card>
          )}

          {/* ── Search / filter bar ── */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
            <input value={teacherSearch} onChange={e=>setTeacherSearch(e.target.value)}
              placeholder="Search by name…"
              style={{ ...acBase.inp, marginBottom:0, flex:1, minWidth:160, fontSize:12.5 }}/>
            <select value={teacherFilterCat} onChange={e=>setTeacherFilterCat(e.target.value)}
              style={{ ...acBase.inp, marginBottom:0, fontSize:12.5, width:'auto' }}>
              <option value="all">All Categories</option>
              {categories.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
            <select value={teacherFilterStatus} onChange={e=>setTeacherFilterStatus(e.target.value)}
              style={{ ...acBase.inp, marginBottom:0, fontSize:12.5, width:'auto' }}>
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ fontSize:12, color:'#9aa3b8', marginBottom:10 }}>
            Showing {filteredTeachers.length} of {teachers.length} teacher{teachers.length!==1?'s':''}
          </div>

          {filteredTeachers.map(t => {
            const cats = t.teacher_categories_map?.map(m=>m.teacher_categories).filter(Boolean) || [];
            return (
              <Card key={t.id} style={{ marginBottom:8, padding:'12px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                  {/* Avatar / photo */}
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name}
                      style={{ width:38, height:38, borderRadius:'50%', objectFit:'cover', border:'1px solid #dde3ed', flexShrink:0 }}
                      onError={e=>{ e.target.style.display='none'; }}/>
                  ) : (
                    <div style={{ width:38, height:38, borderRadius:'50%', background:'#e4ecf7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, color:'#1a2a4a', flexShrink:0 }}>
                      {t.name.trim().charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex:1, minWidth:160 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <StatusDot active={t.active}/>
                      <span style={{ fontWeight:600, fontSize:13.5, color:'#1a2233' }}>{t.name}</span>
                      {t.voice_intro_url && <span title="Has voice intro" style={{ fontSize:13 }}>🎙️</span>}
                      {(t.sample_work||[]).length > 0 && <span title={`${t.sample_work.length} sample(s)`} style={{ fontSize:11, color:'#5a6a88', background:'#f0f4fa', border:'1px solid #dde3ed', borderRadius:5, padding:'1px 6px' }}>📁 {t.sample_work.length}</span>}
                    </div>
                    <div style={{ fontSize:11, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace", marginTop:2 }}>
                      Zoom: {t.zoom_meeting_id||'—'} · {t.zoom_accounts?.account_name||'No account'}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap', flex:1 }}>
                    {cats.map(c=><span key={c.name} style={{ fontSize:11, color:c.color, background:`${c.color}18`, border:`1px solid ${c.color}33`, borderRadius:100, padding:'2px 8px' }}>{c.icon} {c.name}</span>)}
                  </div>
                  <Btn small onClick={()=>{ openEditTeacher(t); setAddingTeacher(false); }}>Edit</Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── STUDENTS ── */}
      {!loading && subTab === 'students' && (
        <div>
          <SectionHeader title={`Students (${students.length})`}
            action={<Btn variant="primary" small onClick={()=>{ setAddingStudent(true); setNewStudent({ name:'', email:'', whatsapp:'', pin:'', password:'', subscription_type:'general_english', total_hours:24, notes:'' }); }}>+ Add Student</Btn>}/>

          {/* Add student form */}
          {addingStudent && (
            <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
              <div style={{ fontSize:14, fontWeight:600, color:T.gold, marginBottom:12 }}>New Student</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Field label="Full Name *">
                  <input value={newStudent.name} onChange={e=>setNewStudent(s=>({...s,name:e.target.value}))}
                    placeholder="e.g. Sara Mohamed" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="WhatsApp * (with country code)">
                  <input value={newStudent.whatsapp} onChange={e=>setNewStudent(s=>({...s,whatsapp:e.target.value}))}
                    placeholder="201012345678" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Email (for portal login)">
                  <input type="email" value={newStudent.email} onChange={e=>setNewStudent(s=>({...s,email:e.target.value}))}
                    placeholder="student@email.com" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Password * (student uses this to log in)">
                  <input type="password" value={newStudent.password} onChange={e=>setNewStudent(s=>({...s,password:e.target.value}))}
                    placeholder="Set a login password" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="PIN (4 digits, optional)">
                  <input value={newStudent.pin} onChange={e=>setNewStudent(s=>({...s,pin:e.target.value.replace(/\D/g,'').slice(0,4)}))}
                    maxLength={4} placeholder="Leave blank if not needed" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Subscription Type">
                  <select value={newStudent.subscription_type} onChange={e=>{
                    const hMap = { general_english:24, ielts_standard:48, ielts_premium:9999, custom: newStudent.total_hours };
                    setNewStudent(s=>({...s, subscription_type:e.target.value, total_hours: hMap[e.target.value]||24 }));
                  }} style={{ ...acBase.inp, marginBottom:0 }}>
                    <option value="general_english">General English — 24h/month</option>
                    <option value="ielts_standard">IELTS Standard — 500 AED</option>
                    <option value="ielts_premium">IELTS Premium — 800 AED</option>
                    <option value="custom">Custom</option>
                  </select>
                </Field>
                {newStudent.subscription_type === 'custom' && (
                  <Field label="Total Hours">
                    <input type="number" min={1} value={newStudent.total_hours}
                      onChange={e=>setNewStudent(s=>({...s,total_hours:e.target.value}))}
                      style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                )}
                <Field label="Notes (optional)" style={{ gridColumn:'1/-1' }}>
                  <input value={newStudent.notes} onChange={e=>setNewStudent(s=>({...s,notes:e.target.value}))}
                    placeholder="Any notes about this student…" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <Btn variant="primary" onClick={addStudent}
                  disabled={studentSaving || !(newStudent.name||'').trim() || !(newStudent.whatsapp||'').trim() || !(newStudent.password||'').trim()}>
                  {studentSaving ? 'Saving…' : 'Save Student'}
                </Btn>
                <Btn onClick={()=>setAddingStudent(false)}>Cancel</Btn>
              </div>
            </Card>
          )}

          {/* Edit student form */}
          {editingStudent && (
            <Card style={{ marginBottom:14, border:`1px solid #1a5fa888` }}>
              <div style={{ fontSize:14, fontWeight:600, color:'#1a5fa8', marginBottom:12 }}>Edit Student — {editingStudent.name}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Field label="Full Name *">
                  <input value={editStudentForm.name} onChange={e=>setEditStudentForm(f=>({...f,name:e.target.value}))}
                    placeholder="e.g. Sara Mohamed" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="WhatsApp * (with country code)">
                  <input value={editStudentForm.whatsapp} onChange={e=>setEditStudentForm(f=>({...f,whatsapp:e.target.value}))}
                    placeholder="201012345678" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Email (for portal login)">
                  <input type="email" value={editStudentForm.email} onChange={e=>setEditStudentForm(f=>({...f,email:e.target.value}))}
                    placeholder="student@email.com" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="New Password (leave blank to keep current)">
                  <input type="password" value={editStudentForm.newPassword} onChange={e=>setEditStudentForm(f=>({...f,newPassword:e.target.value}))}
                    placeholder="Leave blank to keep existing" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="PIN (4 digits, optional)">
                  <input value={editStudentForm.pin} onChange={e=>setEditStudentForm(f=>({...f,pin:e.target.value.replace(/\D/g,'').slice(0,4)}))}
                    maxLength={4} placeholder="Leave blank to keep existing" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Subscription Type">
                  <select value={editStudentForm.subscription_type} onChange={e=>setEditStudentForm(f=>({...f,subscription_type:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                    <option value="general_english">General English — 24h/month</option>
                    <option value="ielts_standard">IELTS Standard — 500 AED</option>
                    <option value="ielts_premium">IELTS Premium — 800 AED</option>
                    <option value="custom">Custom</option>
                  </select>
                </Field>
                <Field label="Total Hours">
                  <input type="number" min={1} value={editStudentForm.total_hours}
                    onChange={e=>setEditStudentForm(f=>({...f,total_hours:e.target.value}))}
                    style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Notes (optional)" style={{ gridColumn:'1/-1' }}>
                  <input value={editStudentForm.notes} onChange={e=>setEditStudentForm(f=>({...f,notes:e.target.value}))}
                    placeholder="Any notes about this student…" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <Btn variant="primary" onClick={saveEditStudent}
                  disabled={editStudentSaving || !(editStudentForm.name||'').trim() || !(editStudentForm.whatsapp||'').trim()}>
                  {editStudentSaving ? 'Saving…' : 'Save Changes'}
                </Btn>
                <Btn onClick={()=>{ setEditingStudent(null); setEditStudentForm({}); }}>Cancel</Btn>
              </div>
            </Card>
          )}

          {students.map(s => {
            const activeSubs = (s.subscriptions||[]).filter(x=>x.status==='active');
            const totalH = activeSubs.reduce((a,x)=>a+(Number(x.total_hours)||0),0);
            const usedH  = activeSubs.reduce((a,x)=>a+(Number(x.used_hours)||0),0);
            const remH   = Math.max(0, totalH - usedH);
            const isExpanded = expandedStudent === s.id;
            const loadedSubs = studentSubs[s.id];
            const SUB_TYPES = [
              { value:'general-24h',   label:'General English — 24h/month', hours:24 },
              { value:'ielts-standard',label:'IELTS Standard — 500 AED',    hours:48 },
              { value:'ielts-premium', label:'IELTS Premium — 800 AED',     hours:9999 },
              { value:'custom',        label:'Custom',                       hours:null },
            ];
            return (
              <Card key={s.id} style={{ marginBottom:8, padding:'12px 16px' }}>
                {/* Student summary row */}
                <div style={{ display:'flex', alignItems:'flex-start', gap:10, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:180 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:2 }}>
                      <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2233' }}>{s.name}</div>
                      {!s.active && (
                        <span style={{ fontSize:10.5, fontWeight:700, color:'#7a4a00', background:'#fff8e8', border:'1px solid #f0d890', borderRadius:100, padding:'2px 8px', fontFamily:"'JetBrains Mono',monospace" }}>
                          ⏳ Pending
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:11.5, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace" }}>{s.email || s.whatsapp}</div>
                    {activeSubs.length > 0 && (
                      <div style={{ fontSize:12, color:remH>0?'#1a7a4a':'#c0392b', marginTop:3 }}>
                        {remH.toFixed(1)}h remaining · {activeSubs.length} active subscription{activeSubs.length>1?'s':''}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {!s.active && (
                      <Btn small variant="primary" onClick={async ()=>{
                        await supabase.from('students').update({ active:true }).eq('id', s.id);
                        fetchStudents();
                      }}>✓ Activate</Btn>
                    )}
                    <Btn small variant="primary" onClick={()=>{
                      setExpandedStudent(isExpanded ? null : s.id);
                      if (!isExpanded && !studentSubs[s.id]) fetchStudentSubs(s.id);
                    }}>
                      {isExpanded ? 'Close' : 'Manage'}
                    </Btn>
                    <Btn small onClick={()=>{
                      setEditingStudent(s);
                      setEditStudentForm({ name:s.name||'', email:s.email||'', whatsapp:s.whatsapp||'', pin:s.pin||'', newPassword:'', subscription_type:s.subscription_type||'general_english', total_hours:s.total_hours||24, notes:s.notes||'' });
                    }} style={{ background:'#1a5fa8', color:'#fff', border:'none' }}>
                      Edit
                    </Btn>
                    <Btn small onClick={()=>deleteStudent(s.id)}
                      style={{ background:'#c0392b', color:'#fff', border:'none' }}>
                      Delete
                    </Btn>
                  </div>
                </div>

                {/* Expanded student panel */}
                {isExpanded && (
                  <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}` }}>

                    {/* Progress dashboard */}
                    <div style={{ marginBottom:20 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:'#1a2233', marginBottom:10 }}>Writing Progress</div>
                      <ProgressDashboard student={s}/>
                    </div>

                    {/* Current teacher assignments */}
                    <div style={{ fontSize:13, fontWeight:600, color:'#1a2233', marginBottom:8 }}>Assigned Teachers</div>
                    {!loadedSubs ? (
                      <div style={{ color:'#9aa3b8', fontSize:12 }}>Loading…</div>
                    ) : loadedSubs.filter(x=>x.teacher_id).length === 0 ? (
                      <div style={{ color:'#9aa3b8', fontSize:12.5, marginBottom:12, fontStyle:'italic' }}>No teachers assigned yet.</div>
                    ) : (
                      <div style={{ marginBottom:12 }}>
                        {loadedSubs.filter(x=>x.teacher_id).map(sub=>(
                          <div key={sub.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#f0f6ff', border:'1px solid #c0d8f8', borderRadius:8, marginBottom:6, flexWrap:'wrap' }}>
                            <div style={{ flex:1, minWidth:140 }}>
                              <div style={{ fontWeight:600, fontSize:13, color:'#1a2233' }}>{sub.teachers?.name || '—'}</div>
                              <div style={{ fontSize:11.5, color:'#5a6a88' }}>
                                {sub.subscription_type} · {sub.total_hours>=9999?'Unlimited':`${Math.max(0,(Number(sub.total_hours)||0)-(Number(sub.used_hours)||0)).toFixed(0)}h remaining`}
                              </div>
                            </div>
                            <Btn small variant="danger" onClick={()=>removeAssignment(sub.id, s.id)}>Remove</Btn>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Assign new teacher */}
                    <div style={{ fontSize:13, fontWeight:600, color:'#1a2233', marginBottom:8 }}>Assign Teacher</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Teacher</MonoLabel>
                        <select value={assignForm.teacher_id} onChange={e=>setAssignForm(f=>({...f,teacher_id:e.target.value}))}
                          style={{ ...acBase.inp, marginBottom:0 }}>
                          <option value="">Select teacher</option>
                          {teachers.filter(t=>t.active).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Subscription Type</MonoLabel>
                        <select value={assignForm.subscription_type} onChange={e=>{
                          const found = SUB_TYPES.find(x=>x.value===e.target.value);
                          setAssignForm(f=>({...f, subscription_type:e.target.value, total_hours: found?.hours||24 }));
                        }} style={{ ...acBase.inp, marginBottom:0 }}>
                          {SUB_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      {assignForm.subscription_type === 'custom' && (
                        <div>
                          <MonoLabel style={{ marginBottom:4 }}>Total Hours</MonoLabel>
                          <input type="number" value={assignForm.total_hours} onChange={e=>setAssignForm(f=>({...f,total_hours:e.target.value}))}
                            style={{ ...acBase.inp, marginBottom:0 }}/>
                        </div>
                      )}
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Start Date</MonoLabel>
                        <input type="date" value={assignForm.start_date} onChange={e=>setAssignForm(f=>({...f,start_date:e.target.value}))}
                          style={{ ...acBase.inp, marginBottom:0 }}/>
                      </div>
                      <div>
                        <MonoLabel style={{ marginBottom:4 }}>Notes (optional)</MonoLabel>
                        <input value={assignForm.notes} onChange={e=>setAssignForm(f=>({...f,notes:e.target.value}))}
                          placeholder="e.g. IELTS target band 7" style={{ ...acBase.inp, marginBottom:0 }}/>
                      </div>
                    </div>
                    <Btn variant="primary" small onClick={()=>assignTeacher(s.id)} disabled={saving||!assignForm.teacher_id}>
                      {saving ? 'Saving…' : '+ Assign Teacher'}
                    </Btn>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── BOOKINGS ── */}
      {!loading && subTab === 'bookings' && (
        <div>
          <SectionHeader title={`All Bookings (${filteredBookings.length})`}/>
          {/* Filter */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
            <button onClick={()=>setFilterTeacher('all')} style={{ padding:'5px 12px', borderRadius:100, border:`1px solid ${filterTeacher==='all'?'#1a2a4a':T.border}`, background:filterTeacher==='all'?'#1a2a4a':'transparent', color:filterTeacher==='all'?'#fff':T.muted, cursor:'pointer', fontSize:12 }}>All</button>
            {teachers.map(t=>(
              <button key={t.id} onClick={()=>setFilterTeacher(t.id)}
                style={{ padding:'5px 12px', borderRadius:100, border:`1px solid ${filterTeacher===t.id?T.gold:T.border}`, background:filterTeacher===t.id?T.gold+'22':'transparent', color:filterTeacher===t.id?T.gold:T.muted, cursor:'pointer', fontSize:12 }}>
                {t.name}
              </button>
            ))}
          </div>

          {filteredBookings.length === 0 && <div style={{ color:'#9aa3b8', fontSize:13, padding:'20px 0', textAlign:'center' }}>No bookings yet.</div>}
          {filteredBookings.map(b => {
            const isPending = b.status === 'pending';
            const sameAccOverlap = bookings.filter(x =>
              x.id !== b.id && x.zoom_account_id === b.zoom_account_id &&
              !['cancelled','pending'].includes(x.status) &&
              x.time_slots?.start_time && b.time_slots?.start_time &&
              x.time_slots.start_time < b.time_slots?.end_time &&
              x.time_slots.end_time   > b.time_slots?.start_time
            ).length;
            const conflict = sameAccOverlap >= 2;

            const cardBorder = isPending
              ? '1.5px solid #e67e22'
              : conflict
                ? '1.5px solid #e74c3c44'
                : '1px solid #dde3ed';
            const cardBg = isPending ? '#fffaf4' : '#fff';

            return (
              <Card key={b.id} style={{ marginBottom:8, padding:'12px 16px', border:cardBorder, background:cardBg }}>
                {isPending && (
                  <div style={{ fontSize:11, fontWeight:700, color:'#b8620a', background:'#fff3e0', border:'1px solid #f0d4a0', borderRadius:6, padding:'4px 10px', display:'inline-block', marginBottom:8, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em' }}>
                    ⏳ PENDING CONFIRMATION
                  </div>
                )}
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-start' }}>
                  <div style={{ flex:1, minWidth:160 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:'#1a2233', marginBottom:2 }}>
                      {b.students?.name||'Unknown'} → {b.teachers?.name||'Unknown'}
                    </div>
                    <div style={{ fontSize:12, color:'#5a6a88', marginBottom:2 }}>
                      {b.time_slots?.start_time ? fmtDateTime(b.time_slots.start_time) : 'TBC'}
                      {b.group_size > 1 && ` · ${b.group_size} students`}
                      {' · '}{b.zoom_accounts?.account_name||'No Zoom'}
                    </div>
                    {b.students?.whatsapp && (
                      <div style={{ fontSize:11.5, color:'#1a5fa8', fontFamily:"'JetBrains Mono',monospace" }}>
                        Student WA: {b.students.whatsapp}
                      </div>
                    )}
                    {b.notes && <div style={{ fontSize:11.5, color:'#9aa3b8', fontStyle:'italic', marginTop:2 }}>{b.notes}</div>}
                    {conflict && <div style={{ fontSize:11.5, color:'#e74c3c', fontWeight:600, marginTop:3 }}>⚠️ Zoom room conflict on this account</div>}
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                    <span style={acBase.statusBadge(b.status)}>{b.status}</span>
                    {b.zoom_link && (
                      <a href={b.zoom_link} target="_blank" rel="noreferrer"
                        style={{ fontSize:11.5, color:'#1a5fa8', background:'#e8f0fb', border:'1px solid #c0d8f8', borderRadius:5, padding:'3px 8px', textDecoration:'none' }}>
                        Zoom
                      </a>
                    )}
                    {isPending && (
                      <Btn small variant="primary" onClick={()=>confirmBooking(b)}>✓ Confirm</Btn>
                    )}
                    {isPending && (
                      <Btn small variant="danger" onClick={()=>updateBookingStatus(b.id,'cancelled')}>Decline</Btn>
                    )}
                    {b.status === 'confirmed' && (
                      <Btn small variant="danger" onClick={()=>openCancelModal(b)}>Cancel</Btn>
                    )}
                    {b.status === 'confirmed' && (
                      <Btn small onClick={()=>completeBooking(b)}>Complete ✓</Btn>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── CATEGORIES ── */}
      {!loading && subTab === 'categories' && (
        <div>
          <SectionHeader title="Categories"
            action={<Btn variant="primary" small onClick={()=>setAddingCat(true)}>+ Add Category</Btn>}/>

          {addingCat && (
            <Card style={{ marginBottom:12, border:`1px solid ${T.gold}44` }}>
              <div style={{ fontSize:13.5, fontWeight:600, color:T.gold, marginBottom:10 }}>New Category</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Field label="Name"><input value={newCat.name} onChange={e=>setNewCat(c=>({...c,name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                <Field label="Icon (emoji)"><input value={newCat.icon} onChange={e=>setNewCat(c=>({...c,icon:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                <Field label="Text Color (hex)"><input value={newCat.color} onChange={e=>setNewCat(c=>({...c,color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                <Field label="Background Color (hex)"><input value={newCat.bg_color} onChange={e=>setNewCat(c=>({...c,bg_color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:10 }}>
                <Btn variant="primary" small onClick={saveCategory} disabled={saving}>Save</Btn>
                <Btn small onClick={()=>setAddingCat(false)}>Cancel</Btn>
              </div>
            </Card>
          )}

          {categories.map(c => (
            <Card key={c.id} style={{ marginBottom:8, padding:'12px 16px' }}>
              {editCat?.id === c.id ? (
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                    <Field label="Name"><input value={editCat.name} onChange={e=>setEditCat(x=>({...x,name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                    <Field label="Icon"><input value={editCat.icon} onChange={e=>setEditCat(x=>({...x,icon:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                    <Field label="Color"><input value={editCat.color} onChange={e=>setEditCat(x=>({...x,color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                    <Field label="Bg Color"><input value={editCat.bg_color} onChange={e=>setEditCat(x=>({...x,bg_color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/></Field>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <Btn variant="primary" small onClick={saveCategory} disabled={saving}>Save</Btn>
                    <Btn small onClick={()=>setEditCat(null)}>Cancel</Btn>
                    <Btn small variant="danger" onClick={async()=>{ if(window.confirm('Delete this category?')){await supabase.from('teacher_categories').delete().eq('id',c.id);fetchCategories();} }}>Delete</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:20 }}>{c.icon}</span>
                  <span style={{ flex:1, fontWeight:600, fontSize:13.5, color:c.color }}>{c.name}</span>
                  <StatusDot active={c.active}/>
                  <Btn small onClick={()=>setEditCat({...c})}>Edit</Btn>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* ── ZOOM ACCOUNTS ── */}
      {!loading && subTab === 'zoom' && (
        <div>
          <SectionHeader title={`Zoom Accounts (${zoomAccounts.length})`}
            action={<Btn variant="primary" small onClick={()=>{ setAddingZoom(true); setNewZoom({ account_name:'', max_simultaneous:2 }); }}>+ Add Zoom Account</Btn>}/>

          {/* Add form */}
          {addingZoom && (
            <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
              <div style={{ fontSize:14, fontWeight:600, color:T.gold, marginBottom:12 }}>New Zoom Account</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:10, alignItems:'end' }}>
                <Field label="Account Name">
                  <input value={newZoom.account_name} onChange={e=>setNewZoom(z=>({...z,account_name:e.target.value}))}
                    placeholder="e.g. Academy Zoom 4" style={{ ...acBase.inp, marginBottom:0 }}/>
                </Field>
                <Field label="Max Simultaneous Rooms">
                  <input type="number" min={1} max={10} value={newZoom.max_simultaneous}
                    onChange={e=>setNewZoom(z=>({...z,max_simultaneous:e.target.value}))}
                    style={{ ...acBase.inp, marginBottom:0, width:80 }}/>
                </Field>
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <Btn variant="primary" small onClick={saveZoomAccount} disabled={zoomSaving || !newZoom.account_name.trim()}>
                  {zoomSaving ? 'Saving…' : 'Save Account'}
                </Btn>
                <Btn small onClick={()=>setAddingZoom(false)}>Cancel</Btn>
              </div>
            </Card>
          )}

          {zoomAccounts.length === 0 && (
            <div style={{ color:'#9aa3b8', textAlign:'center', padding:'30px 0', fontSize:13 }}>No Zoom accounts found. Add one above.</div>
          )}

          {zoomAccounts.map(z => {
            const assigned = teachers.filter(t => t.zoom_account_id === z.id);
            const isEditing = editZoom?.id === z.id;
            return (
              <Card key={z.id} style={{ marginBottom:10 }}>
                {isEditing ? (
                  <div>
                    <div style={{ fontSize:13.5, fontWeight:600, color:T.gold, marginBottom:10 }}>Edit Account</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:10, alignItems:'end', marginBottom:10 }}>
                      <Field label="Account Name">
                        <input value={editZoom.account_name}
                          onChange={e=>setEditZoom(z=>({...z,account_name:e.target.value}))}
                          style={{ ...acBase.inp, marginBottom:0 }}/>
                      </Field>
                      <Field label="Max Simultaneous Rooms">
                        <input type="number" min={1} max={10} value={editZoom.max_simultaneous||2}
                          onChange={e=>setEditZoom(z=>({...z,max_simultaneous:e.target.value}))}
                          style={{ ...acBase.inp, marginBottom:0, width:80 }}/>
                      </Field>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <Btn variant="primary" small
                        onClick={()=>updateZoomAccount(z.id,{ account_name:editZoom.account_name.trim(), max_simultaneous:Number(editZoom.max_simultaneous)||2 })}
                        disabled={zoomSaving||!editZoom.account_name.trim()}>
                        {zoomSaving?'Saving…':'Save'}
                      </Btn>
                      <Btn small onClick={()=>setEditZoom(null)}>Cancel</Btn>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:14.5, color:'#1a2233', marginBottom:3 }}>📹 {z.account_name}</div>
                      <div style={{ fontSize:12, color:'#5a6a88', marginBottom:6 }}>
                        <span style={{ marginRight:10 }}>Max simultaneous rooms: <strong>{z.max_simultaneous||2}</strong></span>
                        <span style={{ color: assigned.length>0?'#1a5fa8':'#9aa3b8' }}>
                          {assigned.length} teacher{assigned.length!==1?'s':''} assigned
                        </span>
                      </div>
                      {assigned.length > 0 ? (
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          {assigned.map(t=>(
                            <span key={t.id} style={{ fontSize:11.5, color:'#1a2233', background:'#f0f4fa', border:'1px solid #dde3ed', borderRadius:6, padding:'3px 9px', display:'flex', alignItems:'center', gap:5 }}>
                              {t.photo_url ? (
                                <img src={t.photo_url} alt="" style={{ width:16, height:16, borderRadius:'50%', objectFit:'cover' }} onError={e=>{ e.target.style.display='none'; }}/>
                              ) : (
                                <span style={{ width:16, height:16, borderRadius:'50%', background:'#c8d8f0', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:'#1a2a4a' }}>{t.name.charAt(0)}</span>
                              )}
                              {t.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize:12, color:'#b0b8cc', fontStyle:'italic' }}>No teachers assigned to this account yet.</div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <Btn small onClick={()=>setEditZoom({ id:z.id, account_name:z.account_name, max_simultaneous:z.max_simultaneous||2 })}>Edit</Btn>
                      <button onClick={()=>deleteZoomAccount(z)}
                        style={{ fontSize:12, color: assigned.length>0?'#9aa3b8':'#c0392b', border:`1px solid ${assigned.length>0?'#dde3ed':'rgba(192,57,43,0.25)'}`, background: assigned.length>0?'#f7f9fc':'rgba(192,57,43,0.06)', borderRadius:7, padding:'5px 10px', cursor: assigned.length>0?'not-allowed':'pointer', opacity: assigned.length>0?0.5:1 }}
                        title={assigned.length>0?'Remove all assigned teachers first':'Delete account'}>
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── SPEAKING EXAMS ── */}
      {!loading && subTab === 'speaking' && (() => {
        const now = new Date();
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const thisMonthExams = speakingExams.filter(e => e.created_at >= thisMonthStart);
        const reportsSent    = speakingExams.filter(e => e.status === 'report_sent').length;
        const pending        = speakingExams.filter(e => e.status === 'scheduled').length;

        const filtered = speakingExams.filter(e =>
          (spFilter.examiner === 'all' || e.examiner_id === spFilter.examiner) &&
          (spFilter.status   === 'all' || e.status      === spFilter.status)
        );

        return (
          <div>
            <SectionHeader title="🎯 Speaking Exams"
              action={<Btn variant="primary" small onClick={()=>setAddingExam(true)}>+ Add Booking</Btn>}/>

            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:16 }}>
              {[
                ['This Month', thisMonthExams.length, '#1a5fa8'],
                ['Reports Sent', reportsSent, '#1a7a4a'],
                ['Pending', pending, pending>0?'#b8620a':'#9aa3b8'],
              ].map(([lbl,val,col])=>(
                <div key={lbl} style={{ background:'#f7f9fc', borderRadius:9, padding:'12px 16px', textAlign:'center', border:`1px solid ${col}33` }}>
                  <div style={{ fontSize:24, fontWeight:700, color:col, fontFamily:"'Cormorant Garamond',serif" }}>{val}</div>
                  <div style={{ fontSize:11.5, color:'#5a6a88' }}>{lbl}</div>
                </div>
              ))}
            </div>

            {/* Add form */}
            {addingExam && (
              <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
                <div style={{ fontSize:13.5, fontWeight:600, color:T.gold, marginBottom:12 }}>Add Speaking Exam Booking</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <Field label="Student Name">
                    <input value={newExam.student_name} onChange={e=>setNewExam(x=>({...x,student_name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                  <Field label="Student WhatsApp">
                    <input value={newExam.student_whatsapp} onChange={e=>setNewExam(x=>({...x,student_whatsapp:e.target.value}))} placeholder="201012345678" style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                  <Field label="Examiner">
                    <select value={newExam.examiner_id} onChange={e=>setNewExam(x=>({...x,examiner_id:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                      <option value="">Select examiner</option>
                      {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Scheduled Date & Time">
                    <input type="datetime-local" value={newExam.scheduled_at} onChange={e=>setNewExam(x=>({...x,scheduled_at:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                  <Field label="Booking Type">
                    <select value={newExam.booking_type} onChange={e=>setNewExam(x=>({...x,booking_type:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                      <option value="package">Package (included)</option>
                      <option value="visitor">Visitor (fee)</option>
                    </select>
                  </Field>
                  <Field label="Notes">
                    <input value={newExam.notes} onChange={e=>setNewExam(x=>({...x,notes:e.target.value}))} placeholder="Optional" style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <Btn variant="primary" onClick={async()=>{
                    if (!newExam.student_name || !newExam.student_whatsapp || !newExam.examiner_id) return;
                    await supabase.from('speaking_exams').insert({
                      ...newExam,
                      scheduled_at: newExam.scheduled_at ? new Date(newExam.scheduled_at).toISOString() : null,
                      status:'scheduled',
                    });
                    setAddingExam(false);
                    setNewExam({ student_name:'', student_whatsapp:'', examiner_id:'', scheduled_at:'', booking_type:'package', notes:'' });
                    fetchSpeakingExams();
                  }}>Save</Btn>
                  <Btn onClick={()=>setAddingExam(false)}>Cancel</Btn>
                </div>
              </Card>
            )}

            {/* Filters */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
              <select value={spFilter.examiner} onChange={e=>setSpFilter(f=>({...f,examiner:e.target.value}))}
                style={{ padding:'6px 10px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:12.5, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                <option value="all">All Examiners</option>
                {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select value={spFilter.status} onChange={e=>setSpFilter(f=>({...f,status:e.target.value}))}
                style={{ padding:'6px 10px', border:'1px solid #dde3ed', borderRadius:7, background:'#f7f9fc', color:'#1a2233', fontSize:12.5, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                <option value="all">All Statuses</option>
                <option value="scheduled">Scheduled</option>
                <option value="report_sent">Report Sent</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {filtered.length === 0 && <div style={{ color:'#9aa3b8', fontSize:13, padding:'20px 0', textAlign:'center' }}>No speaking exams found.</div>}
            {filtered.map(e => {
              const sent = e.status === 'report_sent';
              return (
                <Card key={e.id} style={{ marginBottom:8, padding:'12px 16px', border:sent?'1px solid #c8ead8':'1px solid #dde3ed', background:sent?'#f9fdf9':'#fff' }}>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-start' }}>
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2233', marginBottom:2 }}>{e.student_name}</div>
                      <div style={{ fontSize:12, color:'#1a5fa8', fontFamily:"'JetBrains Mono',monospace", marginBottom:2 }}>WA: {e.student_whatsapp}</div>
                      <div style={{ fontSize:12, color:'#5a6a88' }}>
                        {e.teachers?.name||'No examiner assigned'} · {e.scheduled_at ? fmtDateTime(e.scheduled_at) : 'Date TBC'}
                        {' · '}
                        <span style={{ fontSize:11, fontWeight:600, color:e.booking_type==='package'?'#1a5fa8':'#b8620a' }}>
                          {e.booking_type==='package'?'Package':'Visitor'}
                        </span>
                      </div>
                      {sent && e.overall_score && (
                        <div style={{ fontSize:12, color:'#1a7a4a', fontWeight:600, marginTop:3 }}>⭐ Overall Band: {e.overall_score}</div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:100,
                        color:  sent?'#1a7a4a':e.status==='cancelled'?'#c0392b':'#1a5fa8',
                        background: sent?'#e8f5ee':e.status==='cancelled'?'#fff5f5':'#e8f0fb',
                        border: `1px solid ${sent?'#c8ead8':e.status==='cancelled'?'rgba(192,57,43,0.2)':'#c0d8f8'}` }}>
                        {sent ? '✓ Report Sent' : e.status==='cancelled'?'Cancelled':'⏳ Scheduled'}
                      </span>
                      {e.status === 'scheduled' && (
                        <Btn small variant="danger" onClick={async()=>{ await supabase.from('speaking_exams').update({status:'cancelled'}).eq('id',e.id); fetchSpeakingExams(); }}>Cancel</Btn>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        );
      })()}

      {/* ── PAYROLL ── */}
      {subTab === 'payroll' && <PayrollAdmin/>}

      {/* ── REMINDERS ── */}
      {subTab === 'reminders' && (() => {
        const REMINDER_KEY = 'ca_reminder_log';
        const reminderLog  = (() => { try { return JSON.parse(localStorage.getItem(REMINDER_KEY)||'{}'); } catch { return {}; } })();

        function saveReminderLog(studentId) {
          const log = { ...reminderLog, [studentId]: { sentAt: new Date().toISOString() } };
          localStorage.setItem(REMINDER_KEY, JSON.stringify(log));
        }

        // Build student list with hours data from subscriptions
        const studentData = students.map(s => {
          const activeSubs = (s.subscriptions||[]).filter(x => x.status === 'active');
          const totalH  = activeSubs.reduce((a,x) => a + (Number(x.total_hours)||0), 0);
          const usedH   = activeSubs.reduce((a,x) => a + (Number(x.used_hours)||0), 0);
          const remH    = Math.max(0, totalH - usedH);
          const pct     = totalH > 0 ? (remH / totalH) * 100 : 100;
          return { ...s, totalH, usedH, remH, pct };
        }).filter(s => s.totalH > 0);

        const critical = studentData.filter(s => s.pct < 10);
        const warning  = studentData.filter(s => s.pct >= 10 && s.pct < 20);
        const ok       = studentData.filter(s => s.pct >= 20);

        function sendReminder(s) {
          const wa = (s.whatsapp || '').replace(/\D/g, '');
          if (!wa) return; // skip students with no WhatsApp number
          const msg = `Hello ${s.name}, this is a reminder from Compass Academy. You have ${s.remH.toFixed(1)} hours remaining in your subscription. To continue your lessons without interruption, please contact Mr. Ahmed Salem to renew. wa.me/${ADMIN_WA}`;
          window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, '_blank');
          saveReminderLog(s.id);
        }

        function sendAllCritical() {
          critical.forEach((s, i) => {
            setTimeout(() => sendReminder(s), i * 800);
          });
        }

        const StudentRow = ({ s, urgency }) => {
          const lastSent = reminderLog[s.id]?.sentAt;
          return (
            <div style={{ background:'#fff', border:`1px solid ${urgency==='critical'?'rgba(192,57,43,0.25)':urgency==='warning'?'#f0d890':'#dde3ed'}`, borderRadius:9, padding:'12px 16px', marginBottom:7, display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ width:10, height:10, borderRadius:'50%', background:urgency==='critical'?'#e74c3c':urgency==='warning'?'#e67e22':'#27ae60', flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:160 }}>
                <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2233', marginBottom:1 }}>{s.name}</div>
                <div style={{ fontSize:12, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace" }}>{s.whatsapp}</div>
                <div style={{ marginTop:5, background:'#e4ecf7', borderRadius:4, height:5, width:160, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${s.pct}%`, background:urgency==='critical'?'#e74c3c':urgency==='warning'?'#e67e22':'#27ae60', borderRadius:4 }}/>
                </div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontWeight:700, fontSize:14, color: urgency==='critical'?'#c0392b':urgency==='warning'?'#b8620a':'#1a7a4a' }}>
                  {s.remH.toFixed(1)}h left
                </div>
                <div style={{ fontSize:11, color:'#9aa3b8' }}>{s.pct.toFixed(0)}% remaining</div>
                {lastSent && <div style={{ fontSize:10, color:'#9aa3b8', marginTop:2 }}>Last sent: {new Date(lastSent).toLocaleDateString()}</div>}
              </div>
              <button onClick={() => sendReminder(s)}
                style={{ background:'#25d366', color:'#fff', border:'none', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:12.5, fontWeight:700, display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Send Reminder
              </button>
            </div>
          );
        };

        return (
          <div>
            {/* Stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
              {[['🔴 Critical (<10%)', critical.length, '#e74c3c'], ['🟡 Warning (10-20%)', warning.length, '#e67e22'], ['🟢 OK (>20%)', ok.length, '#27ae60']].map(([l,v,c]) => (
                <div key={l} style={{ background:'#f7f9fc', border:`1px solid ${c}33`, borderRadius:9, padding:'12px 16px', textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:700, color:c, fontFamily:"'Cormorant Garamond',serif" }}>{v}</div>
                  <div style={{ fontSize:12, color:'#5a6a88' }}>{l}</div>
                </div>
              ))}
            </div>

            {critical.length > 0 && (
              <button onClick={sendAllCritical}
                style={{ width:'100%', background:'#c0392b', color:'#fff', border:'none', padding:'12px 0', borderRadius:9, fontSize:14, fontWeight:700, cursor:'pointer', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                🚨 Send All Critical Reminders ({critical.length} students)
              </button>
            )}

            {critical.length > 0 && (
              <>
                <div style={{ fontSize:13, fontWeight:600, color:'#c0392b', marginBottom:8 }}>🔴 Critical — Less than 10% remaining</div>
                {critical.map(s => <StudentRow key={s.id} s={s} urgency="critical"/>)}
                <div style={{ height:16 }}/>
              </>
            )}
            {warning.length > 0 && (
              <>
                <div style={{ fontSize:13, fontWeight:600, color:'#e67e22', marginBottom:8 }}>🟡 Warning — 10–20% remaining</div>
                {warning.map(s => <StudentRow key={s.id} s={s} urgency="warning"/>)}
                <div style={{ height:16 }}/>
              </>
            )}
            {ok.length > 0 && (
              <>
                <div style={{ fontSize:13, fontWeight:600, color:'#27ae60', marginBottom:8 }}>🟢 OK — More than 20% remaining</div>
                {ok.map(s => <StudentRow key={s.id} s={s} urgency="ok"/>)}
              </>
            )}
            {studentData.length === 0 && (
              <div style={{ color:'#9aa3b8', textAlign:'center', padding:'30px 0', fontSize:13 }}>No students with active subscriptions found.</div>
            )}
          </div>
        );
      })()}

      {/* ── CANCELLATION MODAL ── */}
      {cancelModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:16 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, maxWidth:420, width:'100%', boxShadow:'0 8px 32px rgba(26,42,74,0.2)' }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:'#1a2a4a', marginBottom:6 }}>Cancel Booking</div>
            <div style={{ fontSize:13, color:'#5a6a88', marginBottom:18 }}>
              {cancelModal.teachers?.name || 'Teacher'} · {cancelModal.time_slots?.start_time ? fmtDate(cancelModal.time_slots.start_time) : ''}
            </div>

            {/* Step 1: Who cancelled */}
            <div style={{ fontSize:13, fontWeight:600, color:'#1a2233', marginBottom:10 }}>Who cancelled this class?</div>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              {['teacher','student'].map(who => (
                <button key={who} onClick={() => setCancelWho(who)}
                  style={{ flex:1, padding:'11px 0', borderRadius:8, cursor:'pointer', border:`1.5px solid ${cancelWho===who?'#b8860b':'#dde3ed'}`, background:cancelWho===who?'#fffaf0':'#f7f9fc', color:cancelWho===who?'#b8860b':'#5a6a88', fontSize:13.5, fontWeight:cancelWho===who?700:400 }}>
                  {who === 'teacher' ? '👩‍🏫 Teacher' : '👤 Student'}
                </button>
              ))}
            </div>

            {/* Student extra questions */}
            {cancelWho === 'student' && (
              <div style={{ background:'#f7f9fc', border:'1px solid #dde3ed', borderRadius:9, padding:'14px 16px', marginBottom:18 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#1a2233', marginBottom:10 }}>How much notice did the student give?</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
                  {[{ h:0, l:'Same day (0h)' },{ h:24, l:'Less than 24h' },{ h:48, l:'48+ hours' }].map(opt=>(
                    <button key={opt.h} onClick={()=>setCancelNotice(opt.h)}
                      style={{ padding:'7px 14px', borderRadius:7, cursor:'pointer', border:`1.5px solid ${cancelNotice===opt.h?'#1a5fa8':'#dde3ed'}`, background:cancelNotice===opt.h?'#e8f0fb':'transparent', color:cancelNotice===opt.h?'#1a5fa8':'#5a6a88', fontSize:12.5, fontWeight:cancelNotice===opt.h?600:400 }}>
                      {opt.l}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:13, fontWeight:600, color:'#1a2233', marginBottom:8 }}>How many times has this student cancelled this month?</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[1,2,3].map(n=>(
                    <button key={n} onClick={()=>setCancelCount(n)}
                      style={{ flex:1, padding:'7px 0', borderRadius:7, cursor:'pointer', border:`1.5px solid ${cancelCount===n?'#1a5fa8':'#dde3ed'}`, background:cancelCount===n?'#e8f0fb':'transparent', color:cancelCount===n?'#1a5fa8':'#5a6a88', fontSize:13, fontWeight:cancelCount===n?600:400 }}>
                      {n === 1 ? '1st time' : n === 2 ? '2nd time' : '3rd+'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Rule applied preview */}
            {cancelWho && (
              <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16,
                background: cancelWho==='teacher' ? '#fff5f5' : (cancelNotice>=48&&cancelCount<=1) ? '#f7f9fc' : '#fff8e8',
                border: `1px solid ${cancelWho==='teacher'?'rgba(192,57,43,0.2)':(cancelNotice>=48&&cancelCount<=1)?'#dde3ed':'#f0d890'}` }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:'#1a2233', marginBottom:3 }}>Rule applied:</div>
                <div style={{ fontSize:12.5, color:'#5a6a88', lineHeight:1.6 }}>
                  {cancelWho === 'teacher'
                    ? '❌ Teacher cancelled — 0 hours counted. Teacher gets no payment. Make-up required.'
                    : (cancelNotice >= 48 && cancelCount <= 1)
                      ? '✓ Excused cancellation — 0 hours charged to student, 0 hours counted for teacher.'
                      : `⚠️ Charged cancellation — counts as full session for student. Teacher receives 0.5h payment.`}
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:8 }}>
              <button onClick={processCancellation} disabled={!cancelWho || cancelSaving}
                style={{ flex:1, background:'#c0392b', color:'#fff', border:'none', padding:'11px 0', borderRadius:8, fontSize:13.5, fontWeight:600, cursor:'pointer', opacity:(!cancelWho||cancelSaving)?0.5:1 }}>
                {cancelSaving ? 'Processing…' : 'Confirm Cancellation'}
              </button>
              <button onClick={()=>setCancelModal(null)}
                style={{ flex:1, background:'#f7f9fc', border:'1px solid #dde3ed', color:'#5a6a88', padding:'11px 0', borderRadius:8, fontSize:13.5, cursor:'pointer' }}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── COURSES MANAGER ── */}
      {subTab === 'courses' && (() => {
        async function saveCourse() {
          setCourseSaving(true); setCourseMsg('');
          try {
            const payload = {
              name: courseForm.name.trim(), name_ar: courseForm.name_ar.trim()||null,
              cefr_level: courseForm.cefr_level.trim()||null,
              description: courseForm.description.trim()||null, description_ar: courseForm.description_ar.trim()||null,
              icon: courseForm.icon.trim()||'📚', color: courseForm.color, bg_color: courseForm.bg_color,
              border_color: courseForm.border_color, active: courseForm.active,
              sort_order: parseInt(courseForm.sort_order)||0, updated_at: new Date().toISOString(),
            };
            if (editingCourse?.id) {
              await supabase.from('courses').update(payload).eq('id', editingCourse.id);
            } else {
              await supabase.from('courses').insert(payload);
            }
            setCourseMsg('✓ Saved'); setEditingCourse(null); fetchAdminCourses();
          } catch { setCourseMsg('Error saving course'); }
          setCourseSaving(false);
        }

        function openEdit(c) {
          setEditingCourse(c);
          setCourseForm({ name:c.name, name_ar:c.name_ar||'', cefr_level:c.cefr_level||'', description:c.description||'', description_ar:c.description_ar||'', icon:c.icon||'📚', color:c.color||'#1a2a4a', bg_color:c.bg_color||'#f0f4f8', border_color:c.border_color||'#dde3ed', active:c.active, sort_order:String(c.sort_order||0) });
          setCourseMsg('');
        }

        function openAdd() {
          setEditingCourse({ id:null });
          setCourseForm({ name:'', name_ar:'', cefr_level:'', description:'', description_ar:'', icon:'📚', color:'#1a2a4a', bg_color:'#f0f4f8', border_color:'#dde3ed', active:true, sort_order:'0' });
          setCourseMsg('');
        }

        return (
          <div>
            <SectionHeader title={`Courses (${adminCourses.length})`} action={<Btn variant="primary" small onClick={openAdd}>+ Add Course</Btn>}/>
            {courseMsg && <Alert type={courseMsg.startsWith('✓')?'success':'error'} style={{ marginBottom:12 }}>{courseMsg}</Alert>}

            {editingCourse && (
              <Card style={{ marginBottom:16, border:`1px solid ${T.gold}44` }}>
                <div style={{ fontSize:14, fontWeight:600, color:T.gold, marginBottom:12 }}>
                  {editingCourse.id ? `Edit: ${editingCourse.name}` : 'New Course'}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <Field label="Name (English)">
                    <input value={courseForm.name} onChange={e=>setCourseForm(f=>({...f,name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="e.g. IELTS Preparation"/>
                  </Field>
                  <Field label="Name (Arabic)">
                    <input value={courseForm.name_ar} onChange={e=>setCourseForm(f=>({...f,name_ar:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} dir="rtl" placeholder="اختياري"/>
                  </Field>
                  <Field label="CEFR Level">
                    <input value={courseForm.cefr_level} onChange={e=>setCourseForm(f=>({...f,cefr_level:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="A1 / B2 / IELTS / SAT"/>
                  </Field>
                  <Field label="Icon (emoji)">
                    <input value={courseForm.icon} onChange={e=>setCourseForm(f=>({...f,icon:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, fontSize:20 }} placeholder="📚"/>
                  </Field>
                  <Field label="Card Color">
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <input type="color" value={courseForm.color} onChange={e=>setCourseForm(f=>({...f,color:e.target.value}))} style={{ width:38, height:34, border:'none', borderRadius:6, cursor:'pointer' }}/>
                      <input value={courseForm.color} onChange={e=>setCourseForm(f=>({...f,color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, flex:1 }}/>
                    </div>
                  </Field>
                  <Field label="Background Color">
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <input type="color" value={courseForm.bg_color} onChange={e=>setCourseForm(f=>({...f,bg_color:e.target.value}))} style={{ width:38, height:34, border:'none', borderRadius:6, cursor:'pointer' }}/>
                      <input value={courseForm.bg_color} onChange={e=>setCourseForm(f=>({...f,bg_color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, flex:1 }}/>
                    </div>
                  </Field>
                  <Field label="Border Color">
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <input type="color" value={courseForm.border_color} onChange={e=>setCourseForm(f=>({...f,border_color:e.target.value}))} style={{ width:38, height:34, border:'none', borderRadius:6, cursor:'pointer' }}/>
                      <input value={courseForm.border_color} onChange={e=>setCourseForm(f=>({...f,border_color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, flex:1 }}/>
                    </div>
                  </Field>
                  <Field label="Sort Order">
                    <input type="number" value={courseForm.sort_order} onChange={e=>setCourseForm(f=>({...f,sort_order:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                </div>
                <Field label="Description (English)">
                  <textarea value={courseForm.description} onChange={e=>setCourseForm(f=>({...f,description:e.target.value}))} rows={2} style={{ ...acBase.inp, resize:'vertical', marginBottom:0 }}/>
                </Field>
                <Field label="Description (Arabic)">
                  <textarea value={courseForm.description_ar} onChange={e=>setCourseForm(f=>({...f,description_ar:e.target.value}))} rows={2} dir="rtl" style={{ ...acBase.inp, resize:'vertical', marginBottom:0 }}/>
                </Field>
                <Field label="Active">
                  <div style={{ display:'flex', gap:8 }}>
                    {[true,false].map(val=>(
                      <button key={String(val)} onClick={()=>setCourseForm(f=>({...f,active:val}))}
                        style={{ flex:1, padding:'8px', borderRadius:6, cursor:'pointer', border:`1.5px solid ${courseForm.active===val?T.gold:T.border}`, background:courseForm.active===val?T.gold+'22':'transparent', color:courseForm.active===val?T.gold:T.muted, fontSize:12 }}>
                        {val?'Active':'Inactive'}
                      </button>
                    ))}
                  </div>
                </Field>
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <Btn variant="primary" onClick={saveCourse} disabled={courseSaving}>{courseSaving?'Saving…':'Save Course'}</Btn>
                  <Btn variant="ghost" onClick={()=>setEditingCourse(null)}>Cancel</Btn>
                </div>
              </Card>
            )}

            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                  {['Icon','Name','CEFR','Description','Order','Status','Actions'].map(h=>(
                    <th key={h} style={{ textAlign:'left', padding:'8px 10px', color:T.muted, fontWeight:600, fontSize:12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adminCourses.map(c=>(
                  <tr key={c.id} style={{ borderBottom:`1px solid ${T.border}`, opacity:c.active?1:0.5 }}>
                    <td style={{ padding:'10px', fontSize:22 }}>{c.icon}</td>
                    <td style={{ padding:'10px', fontWeight:600, color:'#1a2a4a' }}>{c.name}</td>
                    <td style={{ padding:'10px' }}><span style={{ fontSize:11, background:`${c.color}22`, color:c.color, border:`1px solid ${c.color}44`, borderRadius:100, padding:'2px 8px', fontFamily:"'JetBrains Mono',monospace" }}>{c.cefr_level||'—'}</span></td>
                    <td style={{ padding:'10px', color:'#5a6a88', fontSize:12, maxWidth:200, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>{c.description||''}</td>
                    <td style={{ padding:'10px', fontFamily:"'JetBrains Mono',monospace", fontSize:12 }}>{c.sort_order}</td>
                    <td style={{ padding:'10px' }}>
                      <button onClick={async()=>{ await supabase.from('courses').update({active:!c.active}).eq('id',c.id); fetchAdminCourses(); }}
                        style={{ fontSize:11, padding:'3px 10px', borderRadius:100, cursor:'pointer', border:`1px solid ${c.active?'#c8ead8':'#e8edf5'}`, background:c.active?'#e8f5ee':'#f7f9fc', color:c.active?'#1a7a4a':T.muted }}>
                        {c.active?'Active':'Inactive'}
                      </button>
                    </td>
                    <td style={{ padding:'10px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <Btn variant="ghost" small onClick={()=>openEdit(c)}>Edit</Btn>
                        <Btn variant="ghost" small onClick={async()=>{ if(window.confirm(`Delete "${c.name}"?`)){ await supabase.from('courses').delete().eq('id',c.id); fetchAdminCourses(); } }} style={{ color:'#c0392b' }}>Delete</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ── SUBSCRIPTION TIERS ── */}
      {subTab === 'tiers' && (() => {
        const CURRENCIES = ['AED','USD','EGP','SAR','QAR','KWD'];
        const UNITS = ['days','weeks','months','years','unlimited'];
        const TIER_TYPES = ['full_course','monthly','biweekly','hourly_package','custom'];
        const showHours = ['hourly_package','monthly','biweekly'].includes(tierForm.tier_type);

        async function saveTier() {
          setTierSaving(true); setTierMsg('');
          try {
            const payload = {
              name: tierForm.name.trim(), name_ar: tierForm.name_ar.trim()||null,
              price: parseFloat(tierForm.price)||0, currency: tierForm.currency,
              duration_value: parseInt(tierForm.duration_value)||1, duration_unit: tierForm.duration_unit,
              tier_type: tierForm.tier_type,
              hours_included: tierForm.hours_included ? parseFloat(tierForm.hours_included) : null,
              features: tierForm.features.split('\n').map(f=>f.trim()).filter(Boolean),
              is_active: tierForm.is_active, show_price: tierForm.show_price,
              color: tierForm.color, sort_order: parseInt(tierForm.sort_order)||0,
              course_id: tierForm.course_id||null,
              whatsapp_message: tierForm.whatsapp_message.trim()||null,
              writing_exams_included: tierForm.writing_exams_included ? parseInt(tierForm.writing_exams_included) : null,
              speaking_exams_included: tierForm.speaking_exams_included ? parseInt(tierForm.speaking_exams_included) : null,
              grace_period_days: tierForm.grace_period_days ? parseInt(tierForm.grace_period_days) : null,
              updated_at: new Date().toISOString(),
            };
            if (editingTier?.id) {
              await supabase.from('subscription_tiers').update(payload).eq('id', editingTier.id);
            } else {
              await supabase.from('subscription_tiers').insert(payload);
            }
            setTierMsg('✓ Tier saved'); setEditingTier(null); fetchTiers();
          } catch { setTierMsg('Error saving tier'); }
          setTierSaving(false);
        }

        async function deleteTier(id) {
          if (!window.confirm('Delete this tier?')) return;
          await supabase.from('subscription_tiers').delete().eq('id', id);
          fetchTiers();
        }

        function openEditTier(tier) {
          setEditingTier(tier);
          const feats = Array.isArray(tier.features) ? tier.features : (typeof tier.features === 'string' ? JSON.parse(tier.features||'[]') : []);
          setTierForm({
            name: tier.name, name_ar: tier.name_ar||'', price: String(tier.price), currency: tier.currency,
            duration_value: String(tier.duration_value), duration_unit: tier.duration_unit,
            tier_type: tier.tier_type||'full_course', hours_included: tier.hours_included!=null?String(tier.hours_included):'',
            features: feats.join('\n'), is_active: tier.is_active, show_price: tier.show_price!==false,
            color: tier.color||'#1a2a4a', sort_order: String(tier.sort_order||0),
            course_id: tier.course_id||'', whatsapp_message: tier.whatsapp_message||'',
            writing_exams_included: tier.writing_exams_included!=null?String(tier.writing_exams_included):'',
            speaking_exams_included: tier.speaking_exams_included!=null?String(tier.speaking_exams_included):'',
            grace_period_days: tier.grace_period_days!=null?String(tier.grace_period_days):'60',
          });
        }

        function openAddTier() {
          setEditingTier({ id:null });
          setTierForm({ name:'', name_ar:'', price:'', currency:'AED', duration_value:'1', duration_unit:'months', tier_type:'full_course', hours_included:'', features:'', is_active:true, show_price:true, color:'#1a2a4a', sort_order:'0', course_id:'', whatsapp_message:'', writing_exams_included:'', speaking_exams_included:'', grace_period_days:'60' });
        }

        return (
          <div>
            <SectionHeader title={`Subscription Tiers (${subTiers.length})`} action={<Btn variant="primary" small onClick={openAddTier}>+ Add Tier</Btn>}/>
            {tierMsg && <Alert type={tierMsg.startsWith('✓')?'success':'error'} style={{ marginBottom:12 }}>{tierMsg}</Alert>}

            {editingTier && (
              <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
                <div style={{ fontSize:14, fontWeight:600, color:T.gold, marginBottom:12 }}>
                  {editingTier.id ? `Edit: ${editingTier.name}` : 'New Tier'}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                  <Field label="Tier Name (English)">
                    <input value={tierForm.name} onChange={e=>setTierForm(f=>({...f,name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="e.g. IELTS Standard"/>
                  </Field>
                  <Field label="Tier Name (Arabic)">
                    <input value={tierForm.name_ar} onChange={e=>setTierForm(f=>({...f,name_ar:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} dir="rtl" placeholder="اختياري"/>
                  </Field>
                  <Field label="Price">
                    <input type="number" value={tierForm.price} onChange={e=>setTierForm(f=>({...f,price:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="500"/>
                  </Field>
                  <Field label="Currency">
                    <select value={tierForm.currency} onChange={e=>setTierForm(f=>({...f,currency:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                      {CURRENCIES.map(c=><option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Tier Type">
                    <select value={tierForm.tier_type} onChange={e=>setTierForm(f=>({...f,tier_type:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                      {TIER_TYPES.map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                    </select>
                  </Field>
                  {showHours && (
                    <Field label="Hours Included">
                      <input type="number" min={1} value={tierForm.hours_included} onChange={e=>setTierForm(f=>({...f,hours_included:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="24"/>
                    </Field>
                  )}
                  <Field label="Duration Value">
                    <input type="number" min={1} value={tierForm.duration_value} onChange={e=>setTierForm(f=>({...f,duration_value:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} disabled={tierForm.duration_unit==='unlimited'}/>
                  </Field>
                  <Field label="Duration Unit">
                    <select value={tierForm.duration_unit} onChange={e=>setTierForm(f=>({...f,duration_unit:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                      {UNITS.map(u=><option key={u}>{u}</option>)}
                    </select>
                  </Field>
                  <Field label="Linked Course">
                    <select value={tierForm.course_id} onChange={e=>setTierForm(f=>({...f,course_id:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                      <option value="">— None —</option>
                      {adminCourses.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Color">
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <input type="color" value={tierForm.color} onChange={e=>setTierForm(f=>({...f,color:e.target.value}))} style={{ width:40, height:36, border:'none', borderRadius:6, cursor:'pointer' }}/>
                      <input value={tierForm.color} onChange={e=>setTierForm(f=>({...f,color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, flex:1 }}/>
                    </div>
                  </Field>
                  <Field label="Writing Exams Included">
                    <input type="number" min={0} value={tierForm.writing_exams_included} onChange={e=>setTierForm(f=>({...f,writing_exams_included:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="e.g. 12"/>
                  </Field>
                  <Field label="Speaking Mock Exams Included">
                    <input type="number" min={0} value={tierForm.speaking_exams_included} onChange={e=>setTierForm(f=>({...f,speaking_exams_included:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="e.g. 12"/>
                  </Field>
                  <Field label="Grace Period (days after end)">
                    <input type="number" min={0} value={tierForm.grace_period_days} onChange={e=>setTierForm(f=>({...f,grace_period_days:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="60"/>
                  </Field>
                  <Field label="Sort Order">
                    <input type="number" value={tierForm.sort_order} onChange={e=>setTierForm(f=>({...f,sort_order:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
                  </Field>
                  <Field label="Show Price on Landing Page">
                    <div style={{ display:'flex', gap:8 }}>
                      {[true,false].map(val=>(
                        <button key={String(val)} onClick={()=>setTierForm(f=>({...f,show_price:val}))}
                          style={{ flex:1, padding:'8px', borderRadius:6, cursor:'pointer', border:`1.5px solid ${tierForm.show_price===val?T.gold:T.border}`, background:tierForm.show_price===val?T.gold+'22':'transparent', color:tierForm.show_price===val?T.gold:T.muted, fontSize:12 }}>
                          {val?'Show':'Hide'}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Active">
                    <div style={{ display:'flex', gap:8 }}>
                      {[true,false].map(val=>(
                        <button key={String(val)} onClick={()=>setTierForm(f=>({...f,is_active:val}))}
                          style={{ flex:1, padding:'8px', borderRadius:6, cursor:'pointer', border:`1.5px solid ${tierForm.is_active===val?T.gold:T.border}`, background:tierForm.is_active===val?T.gold+'22':'transparent', color:tierForm.is_active===val?T.gold:T.muted, fontSize:12 }}>
                          {val?'Active':'Inactive'}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
                <Field label="Features (one per line)">
                  <textarea value={tierForm.features} onChange={e=>setTierForm(f=>({...f,features:e.target.value}))} rows={4}
                    placeholder={'Smart Writing feedback\nTest Mode practice\nProgress tracking\nWhatsApp support'}
                    style={{ ...acBase.inp, resize:'vertical', marginBottom:0 }}/>
                </Field>
                <Field label="Custom WhatsApp Enquiry Message (optional)">
                  <input value={tierForm.whatsapp_message} onChange={e=>setTierForm(f=>({...f,whatsapp_message:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="I'm interested in enrolling in…"/>
                </Field>
                <div style={{ display:'flex', gap:8, marginTop:12 }}>
                  <Btn variant="primary" onClick={saveTier} disabled={tierSaving}>{tierSaving?'Saving…':'Save Tier'}</Btn>
                  <Btn variant="ghost" onClick={()=>setEditingTier(null)}>Cancel</Btn>
                </div>
              </Card>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
              {subTiers.map(tier => {
                const feats = Array.isArray(tier.features) ? tier.features : (typeof tier.features === 'string' ? JSON.parse(tier.features||'[]') : []);
                const linkedCourse = adminCourses.find(c=>c.id===tier.course_id);
                return (
                  <div key={tier.id} style={{ background:'#fff', border:`2px solid ${tier.color||T.border}33`, borderRadius:12, padding:'16px', opacity:tier.is_active?1:0.55 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                      <div>
                        <div style={{ fontWeight:700, color:tier.color||'#1a2a4a', fontSize:15 }}>{tier.name}</div>
                        {tier.show_price !== false
                          ? <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:18, fontWeight:700, color:tier.color||T.gold }}>{tier.price} {tier.currency}</div>
                          : <div style={{ fontSize:12, color:T.muted, fontStyle:'italic' }}>Price hidden</div>
                        }
                        <div style={{ fontSize:11, color:T.muted }}>{tier.tier_type?.replace(/_/g,' ')} · {tier.duration_unit==='unlimited'?'Unlimited':`${tier.duration_value} ${tier.duration_unit}`}</div>
                        {(tier.writing_exams_included!=null||tier.speaking_exams_included!=null) && (
                          <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
                            {tier.writing_exams_included!=null && `✍️ ${tier.writing_exams_included} writing`}
                            {tier.writing_exams_included!=null && tier.speaking_exams_included!=null && ' · '}
                            {tier.speaking_exams_included!=null && `🗣️ ${tier.speaking_exams_included} speaking`}
                            {tier.grace_period_days!=null && ` · ${tier.grace_period_days}d grace`}
                          </div>
                        )}
                        {linkedCourse && <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{linkedCourse.icon} {linkedCourse.name}</div>}
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end' }}>
                        {!tier.is_active && <span style={{ fontSize:10, background:'#e8edf5', color:T.muted, borderRadius:100, padding:'2px 8px' }}>Inactive</span>}
                        <button onClick={async()=>{ await supabase.from('subscription_tiers').update({show_price:!tier.show_price}).eq('id',tier.id); fetchTiers(); }}
                          style={{ fontSize:10, padding:'2px 8px', borderRadius:100, cursor:'pointer', border:`1px solid ${tier.show_price!==false?'#c0d8f8':'#e8edf5'}`, background:tier.show_price!==false?'#e8f0fb':'#f7f9fc', color:tier.show_price!==false?'#1a5fa8':T.muted }}>
                          {tier.show_price!==false?'Price shown':'Price hidden'}
                        </button>
                      </div>
                    </div>
                    <div style={{ marginBottom:12 }}>
                      {feats.slice(0,3).map((f,i)=><div key={i} style={{ fontSize:11.5, color:'#5a6a88', marginBottom:3 }}>✓ {f}</div>)}
                      {feats.length > 3 && <div style={{ fontSize:11, color:T.muted }}>+{feats.length-3} more</div>}
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <Btn variant="ghost" small onClick={()=>openEditTier(tier)}>Edit</Btn>
                      <Btn variant="ghost" small onClick={()=>deleteTier(tier.id)} style={{ color:'#c0392b' }}>Delete</Btn>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── BRANDING & SETTINGS ── */}
      {subTab === 'branding' && (
        <div>
          <SectionHeader title="Branding & Settings"/>
          {brandMsg && <Alert type={brandMsg.startsWith('✓')?'success':'error'} style={{ marginBottom:12 }}>{brandMsg}</Alert>}

          {/* Logo upload */}
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontWeight:600, color:'#1a2a4a', marginBottom:12 }}>Logo</div>
            <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
              {brandForm.logo_url
                ? <img src={brandForm.logo_url} alt="logo" style={{ height:56, objectFit:'contain', border:'1px solid #e8edf5', borderRadius:8, padding:4 }}/>
                : <div style={{ width:56, height:56, background:'#f0f4f8', border:'1px solid #e8edf5', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#9aa3b8', fontSize:22 }}>🖼️</div>
              }
              <div style={{ flex:1 }}>
                <label style={{ ...acBase.lbl, marginBottom:4 }}>Upload Logo to Supabase Storage</label>
                <input type="file" accept="image/*" onChange={async e=>{
                  const file = e.target.files?.[0]; if (!file) return;
                  setBrandMsg('Uploading…');
                  const ext = file.name.split('.').pop();
                  const path = `logo/logo_${Date.now()}.${ext}`;
                  console.log('upload path:', path);
                  const { error } = await supabase.storage.from('academy-assets').upload(path, file, { upsert:true });
                  if (error) { setBrandMsg('Upload failed: ' + error.message); return; }
                  const { data: urlData } = supabase.storage.from('academy-assets').getPublicUrl(path);
                  const publicUrl = urlData.publicUrl;
                  console.log('publicUrl:', publicUrl);
                  setBrandForm(f=>({...f, logo_url: publicUrl}));
                  // Immediately persist so the logo appears site-wide without clicking Save
                  const _now = new Date().toISOString();
                  const { data: _upd } = await supabase.from('academy_settings')
                    .update({ setting_value: publicUrl, updated_at: _now })
                    .eq('setting_key', 'logo_url')
                    .select('setting_key');
                  let saveErr = null;
                  if (!_upd || _upd.length === 0) {
                    const { error: _insErr } = await supabase.from('academy_settings')
                      .insert({ setting_key: 'logo_url', setting_value: publicUrl, updated_at: _now });
                    saveErr = _insErr;
                  }
                  console.log('upsert error:', saveErr);
                  setBrandMsg(saveErr ? '✓ Uploaded — click Save to apply' : '✓ Logo saved and live');
                }} style={{ display:'block', marginBottom:8 }}/>
                <Field label="Or paste URL directly">
                  <input value={brandForm.logo_url} onChange={e=>setBrandForm(f=>({...f,logo_url:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="https://…"/>
                </Field>
              </div>
            </div>
          </Card>

          <Card style={{ maxWidth:700 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Academy Name">
                <input value={brandForm.academy_name} onChange={e=>setBrandForm(f=>({...f,academy_name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
              </Field>
              <Field label="Tagline">
                <input value={brandForm.tagline} onChange={e=>setBrandForm(f=>({...f,tagline:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
              </Field>
              <Field label="WhatsApp Egypt (+20)">
                <input value={brandForm.whatsapp_number} onChange={e=>setBrandForm(f=>({...f,whatsapp_number:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="201008182425"/>
              </Field>
              <Field label="WhatsApp UAE (+971)">
                <input value={brandForm.whatsapp_number_2} onChange={e=>setBrandForm(f=>({...f,whatsapp_number_2:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="971565727742"/>
              </Field>
              <Field label="Primary Color">
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="color" value={brandForm.primary_color} onChange={e=>setBrandForm(f=>({...f,primary_color:e.target.value}))} style={{ width:40, height:36, border:'none', borderRadius:6, cursor:'pointer' }}/>
                  <input value={brandForm.primary_color} onChange={e=>setBrandForm(f=>({...f,primary_color:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, flex:1 }}/>
                </div>
              </Field>
            </div>

            {/* Hero stats */}
            <div style={{ marginTop:16, marginBottom:8, fontSize:13, fontWeight:600, color:'#1a2a4a' }}>Landing Page Hero Stats</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:8, marginBottom:16 }}>
              {[1,2,3,4].map(n=>(
                <div key={n}>
                  <Field label={`Stat ${n} Number`}>
                    <input value={brandForm[`hero_stat_${n}_number`]||''} onChange={e=>setBrandForm(f=>({...f,[`hero_stat_${n}_number`]:e.target.value}))} style={{ ...acBase.inp, marginBottom:4 }} placeholder="Band 7.0+"/>
                  </Field>
                  <Field label={`Stat ${n} Label`}>
                    <input value={brandForm[`hero_stat_${n}_label`]||''} onChange={e=>setBrandForm(f=>({...f,[`hero_stat_${n}_label`]:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="Average Score"/>
                  </Field>
                </div>
              ))}
            </div>
            <div style={{ marginTop:4 }}>
              <Btn variant="primary" disabled={brandSaving} onClick={async () => {
                setBrandSaving(true); setBrandMsg('');
                try {
                  const _ts = new Date().toISOString();
                  for (const [key, value] of Object.entries(brandForm)) {
                    const { data: _upd } = await supabase.from('academy_settings')
                      .update({ setting_value: value||null, updated_at: _ts })
                      .eq('setting_key', key)
                      .select('setting_key');
                    if (!_upd || _upd.length === 0) {
                      await supabase.from('academy_settings')
                        .insert({ setting_key: key, setting_value: value||null, updated_at: _ts });
                    }
                  }
                  setBrandMsg('✓ Settings saved');
                  fetchBranding();
                } catch { setBrandMsg('Error saving settings'); }
                setBrandSaving(false);
              }}>{brandSaving ? 'Saving…' : 'Save All Settings'}</Btn>
            </div>
          </Card>

          {/* Registration Courses editor */}
          <Card style={{ marginTop:16, maxWidth:700 }}>
            <div style={{ fontWeight:600, color:'#1a2a4a', marginBottom:4 }}>Registration Courses</div>
            <div style={{ fontSize:12.5, color:T.muted, marginBottom:14, lineHeight:1.6 }}>
              Courses shown as selectable chips on the student registration form. Students pick one to indicate their interest — no prices are shown.
            </div>
            {regCourseMsg && <Alert type={regCourseMsg.startsWith('✓')?'success':'error'} style={{ marginBottom:10 }}>{regCourseMsg}</Alert>}
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
              {regCourses.map((c, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:4, background:'#fff8e8', border:'1.5px solid #f0d890', borderRadius:8, padding:'5px 10px', fontSize:13, color:'#7a4a00' }}>
                  <span>{c}</span>
                  <button onClick={() => setRegCourses(prev => prev.filter((_,j) => j !== i))}
                    style={{ background:'none', border:'none', color:'#b8860b', cursor:'pointer', fontSize:15, lineHeight:1, padding:'0 0 0 4px', fontWeight:700 }}>×</button>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:14 }}>
              <input value={regCourseInput} onChange={e => setRegCourseInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && regCourseInput.trim()) { setRegCourses(prev => [...prev, regCourseInput.trim()]); setRegCourseInput(''); }}}
                placeholder="New course name…"
                style={{ ...acBase.inp, marginBottom:0, flex:1 }}/>
              <Btn variant="secondary" onClick={() => { if (regCourseInput.trim()) { setRegCourses(prev => [...prev, regCourseInput.trim()]); setRegCourseInput(''); }}}>Add</Btn>
            </div>
            <Btn variant="primary" disabled={regCourseSaving} onClick={async () => {
              setRegCourseSaving(true); setRegCourseMsg('');
              try {
                const value = JSON.stringify(regCourses);
                const ts = new Date().toISOString();
                const { data: upd } = await supabase.from('academy_settings')
                  .update({ setting_value: value, updated_at: ts })
                  .eq('setting_key', 'registration_courses')
                  .select('setting_key');
                if (!upd || upd.length === 0) {
                  await supabase.from('academy_settings')
                    .insert({ setting_key: 'registration_courses', setting_value: value, updated_at: ts });
                }
                setRegCourseMsg('✓ Courses saved');
                fetchBranding();
              } catch { setRegCourseMsg('Error saving courses'); }
              setRegCourseSaving(false);
            }}>{regCourseSaving ? 'Saving…' : 'Save Courses'}</Btn>
          </Card>

          {/* Course image upload section */}
          <div style={{ marginTop:24 }}>
            <SectionHeader title="Course Cover Images"/>
            <div style={{ fontSize:13, color:T.muted, marginBottom:12 }}>
              Upload cover images for courses via Supabase Storage at <code>academy-assets/courses/{'{'}{'{'}course-id{'}'}{'}'}</code> and paste the URL below.
            </div>
            <CourseCoverImages/>
          </div>
        </div>
      )}

      {/* ── RESULTS & TESTIMONIALS (FIX 17) ── */}
      {subTab === 'results' && (() => {
        const COUNTRIES = ['Egypt','UAE','Saudi Arabia','Qatar','Kuwait','Other'];

        async function saveResult() {
          setResultSaving(true); setResultMsg('');
          try {
            const payload = { ...resultForm, sort_order: parseInt(resultForm.sort_order)||0 };
            const { error } = editingResult?.id
              ? await supabase.from('student_results').update(payload).eq('id', editingResult.id)
              : await supabase.from('student_results').insert(payload);
            if (error) { setResultMsg('Save failed: ' + error.message); setResultSaving(false); return; }
            setEditingResult(null);
            fetchResults();
          } catch(e) { setResultMsg('Error: ' + e.message); }
          setResultSaving(false);
        }

        async function saveTesti() {
          setTestiSaving(true);
          try {
            const payload = { ...testiForm, rating: parseInt(testiForm.rating)||5 };
            if (editingTesti?.id) {
              await supabase.from('testimonials').update(payload).eq('id', editingTesti.id);
            } else {
              await supabase.from('testimonials').insert(payload);
            }
            setEditingTesti(null);
            fetchResults();
          } catch {}
          setTestiSaving(false);
        }

        const unreadEnquiries = enquiries.filter(e=>e.status==='new').length;

        return (
          <div>
            {/* Sub-navigation */}
            <div style={{ display:'flex', gap:8, marginBottom:18 }}>
              {[['results',`🏆 Results (${results.length})`],['testimonials',`💬 Testimonials (${testimonials.length})`],['enquiries',`📬 Enquiries${unreadEnquiries>0?` (${unreadEnquiries} new)`:''}`]].map(([k,l])=>(
                <button key={k} onClick={()=>setResultsSubTab(k)}
                  style={{ padding:'6px 14px', borderRadius:6, border:`1px solid ${resultsSubTab===k?T.gold:T.border}`, background:resultsSubTab===k?T.gold+'22':'transparent', color:resultsSubTab===k?T.gold:T.muted, cursor:'pointer', fontSize:12.5 }}>
                  {l}
                </button>
              ))}
            </div>

            {/* RESULTS */}
            {resultsSubTab === 'results' && (
              <div>
                <SectionHeader title="Student Results" action={<Btn variant="primary" small onClick={()=>{ setEditingResult({id:null}); setResultForm({student_name:'',country:'Egypt',before_score:'',after_score:'',comment:'',screenshot_url:'',is_featured:false,sort_order:'0'}); setResultMsg(''); }}>+ Add Result</Btn>}/>
                {editingResult && (
                  <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
                    {resultMsg && <Alert type={resultMsg.startsWith('✓')?'success':'error'} style={{ marginBottom:10 }}>{resultMsg}</Alert>}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <Field label="Student First Name">
                        <input value={resultForm.student_name} onChange={e=>setResultForm(f=>({...f,student_name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="Ahmed"/>
                      </Field>
                      <Field label="Country">
                        <select value={resultForm.country} onChange={e=>setResultForm(f=>({...f,country:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                          {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Before Score (e.g. 5.5)">
                        <input value={resultForm.before_score} onChange={e=>setResultForm(f=>({...f,before_score:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="5.5"/>
                      </Field>
                      <Field label="After Score (e.g. 7.0)">
                        <input value={resultForm.after_score} onChange={e=>setResultForm(f=>({...f,after_score:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }} placeholder="7.0"/>
                      </Field>
                      <Field label="Sort Order">
                        <input type="number" value={resultForm.sort_order} onChange={e=>setResultForm(f=>({...f,sort_order:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
                      </Field>
                    </div>
                    <Field label="Comment / Student Quote">
                      <textarea value={resultForm.comment} onChange={e=>setResultForm(f=>({...f,comment:e.target.value}))} rows={2} style={{ ...acBase.inp, resize:'vertical', marginBottom:0 }} placeholder="The course really helped me…"/>
                    </Field>

                    {/* Screenshot upload */}
                    <div style={{ marginTop:12, padding:12, background:'#f8f9fb', border:'1px solid #e8edf5', borderRadius:8 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#1a2a4a', marginBottom:8 }}>Result Screenshot (optional)</div>
                      <div style={{ display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap' }}>
                        {resultForm.screenshot_url && (
                          <div style={{ position:'relative' }}>
                            <img src={resultForm.screenshot_url} alt="screenshot" style={{ height:72, width:120, objectFit:'cover', borderRadius:6, border:'1px solid #e8edf5' }}/>
                            <button onClick={()=>setResultForm(f=>({...f,screenshot_url:''}))} style={{ position:'absolute', top:-6, right:-6, background:'#c0392b', color:'#fff', border:'none', borderRadius:'50%', width:18, height:18, fontSize:10, cursor:'pointer', lineHeight:'18px' }}>✕</button>
                          </div>
                        )}
                        <div style={{ flex:1, minWidth:200 }}>
                          <label style={{ ...acBase.lbl, marginBottom:4 }}>Upload image</label>
                          <input type="file" accept="image/*" disabled={resultUploading} onChange={async e=>{
                            const file = e.target.files?.[0]; if (!file) return;
                            setResultUploading(true); setResultMsg('Uploading…');
                            const ext = file.name.split('.').pop();
                            const path = `results/result_${Date.now()}.${ext}`;
                            const { error: upErr } = await supabase.storage.from('academy-assets').upload(path, file, { upsert:true });
                            if (upErr) { setResultMsg('Upload failed: ' + upErr.message); setResultUploading(false); return; }
                            const { data: urlData } = supabase.storage.from('academy-assets').getPublicUrl(path);
                            setResultForm(f=>({...f, screenshot_url: urlData.publicUrl}));
                            setResultMsg('✓ Image uploaded');
                            setResultUploading(false);
                            e.target.value = '';
                          }} style={{ display:'block', fontSize:12 }}/>
                          <div style={{ fontSize:11, color:T.muted, marginTop:6 }}>or paste URL directly:</div>
                          <input value={resultForm.screenshot_url||''} onChange={e=>setResultForm(f=>({...f,screenshot_url:e.target.value}))} style={{ ...acBase.inp, marginBottom:0, marginTop:4 }} placeholder="https://…"/>
                        </div>
                      </div>
                    </div>

                    <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:12, cursor:'pointer' }}>
                      <input type="checkbox" checked={resultForm.is_featured} onChange={e=>setResultForm(f=>({...f,is_featured:e.target.checked}))} style={{ accentColor:T.gold }}/>
                      <span style={{ fontSize:12.5 }}>Show on landing page</span>
                    </label>
                    <div style={{ display:'flex', gap:8, marginTop:12 }}>
                      <Btn variant="primary" onClick={saveResult} disabled={resultSaving||resultUploading}>{resultSaving?'Saving…':'Save Result'}</Btn>
                      <Btn variant="ghost" onClick={()=>{ setEditingResult(null); setResultMsg(''); }}>Cancel</Btn>
                    </div>
                  </Card>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12 }}>
                  {results.map(r=>(
                    <div key={r.id} style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, padding:14 }}>
                      <div style={{ display:'flex', gap:10, marginBottom:10 }}>
                        <div style={{ textAlign:'center', background:'#fff5f5', border:'1px solid #fecdd3', borderRadius:7, padding:'6px 12px' }}>
                          <div style={{ fontSize:10, color:'#9ca3af' }}>BEFORE</div>
                          <div style={{ fontSize:18, fontWeight:700, color:'#dc2626' }}>{r.before_score}</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center' }}>→</div>
                        <div style={{ textAlign:'center', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:7, padding:'6px 12px' }}>
                          <div style={{ fontSize:10, color:'#9ca3af' }}>AFTER</div>
                          <div style={{ fontSize:18, fontWeight:700, color:'#16a34a' }}>{r.after_score}</div>
                        </div>
                      </div>
                      {r.screenshot_url && <img src={r.screenshot_url} alt="result" style={{ width:'100%', height:80, objectFit:'cover', borderRadius:6, marginBottom:8 }}/>}
                      <div style={{ fontWeight:600, fontSize:13.5, color:'#1a2a4a' }}>{r.student_name}</div>
                      <div style={{ fontSize:12, color:T.muted }}>{r.country} {r.is_featured && '⭐'}</div>
                      {r.comment && <div style={{ fontSize:12, color:'#5a6a88', fontStyle:'italic', marginTop:6, lineHeight:1.5 }}>"{r.comment}"</div>}
                      <div style={{ display:'flex', gap:6, marginTop:10 }}>
                        <Btn variant="ghost" small onClick={()=>{ setEditingResult(r); setResultForm({...r, screenshot_url:r.screenshot_url||'', sort_order:String(r.sort_order||0)}); setResultMsg(''); }}>Edit</Btn>
                        <Btn variant="ghost" small onClick={async()=>{ if(window.confirm('Delete?')){ await supabase.from('student_results').delete().eq('id',r.id); fetchResults(); } }} style={{ color:'#c0392b' }}>Delete</Btn>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TESTIMONIALS */}
            {resultsSubTab === 'testimonials' && (
              <div>
                <SectionHeader title="Testimonials" action={<Btn variant="primary" small onClick={()=>{ setEditingTesti({id:null}); setTestiForm({student_name:'',country:'Egypt',rating:5,text:'',is_featured:false}); }}>+ Add Testimonial</Btn>}/>
                {editingTesti && (
                  <Card style={{ marginBottom:14, border:`1px solid ${T.gold}44` }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <Field label="Student Name">
                        <input value={testiForm.student_name} onChange={e=>setTestiForm(f=>({...f,student_name:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}/>
                      </Field>
                      <Field label="Country">
                        <select value={testiForm.country} onChange={e=>setTestiForm(f=>({...f,country:e.target.value}))} style={{ ...acBase.inp, marginBottom:0 }}>
                          {COUNTRIES.map(c=><option key={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Rating (1-5)">
                        <select value={testiForm.rating} onChange={e=>setTestiForm(f=>({...f,rating:Number(e.target.value)}))} style={{ ...acBase.inp, marginBottom:0 }}>
                          {[5,4,3,2,1].map(n=><option key={n} value={n}>{n} star{n!==1?'s':''}</option>)}
                        </select>
                      </Field>
                      <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:0 }}>
                        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                          <input type="checkbox" checked={testiForm.is_featured} onChange={e=>setTestiForm(f=>({...f,is_featured:e.target.checked}))} style={{ accentColor:T.gold }}/>
                          <span style={{ fontSize:12.5 }}>Show on landing page</span>
                        </label>
                      </div>
                    </div>
                    <Field label="Testimonial Text">
                      <textarea value={testiForm.text} onChange={e=>setTestiForm(f=>({...f,text:e.target.value}))} rows={3} style={{ ...acBase.inp, resize:'vertical', marginBottom:0 }} placeholder="The course was excellent…"/>
                    </Field>
                    <div style={{ display:'flex', gap:8, marginTop:12 }}>
                      <Btn variant="primary" onClick={saveTesti} disabled={testiSaving}>{testiSaving?'Saving…':'Save Testimonial'}</Btn>
                      <Btn variant="ghost" onClick={()=>setEditingTesti(null)}>Cancel</Btn>
                    </div>
                  </Card>
                )}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12 }}>
                  {testimonials.map(t=>(
                    <div key={t.id} style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, padding:14 }}>
                      <div style={{ marginBottom:6 }}>{'★'.repeat(t.rating||0)}{'☆'.repeat(5-(t.rating||0))}</div>
                      <div style={{ fontSize:13, color:'#374151', fontStyle:'italic', lineHeight:1.6, marginBottom:8 }}>"{t.text}"</div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{t.student_name}</div>
                      <div style={{ fontSize:12, color:T.muted }}>{t.country} {t.is_featured && '⭐'}</div>
                      <div style={{ display:'flex', gap:6, marginTop:10 }}>
                        <Btn variant="ghost" small onClick={()=>{ setEditingTesti(t); setTestiForm({student_name:t.student_name,country:t.country||'Egypt',rating:t.rating||5,text:t.text,is_featured:t.is_featured}); }}>Edit</Btn>
                        <Btn variant="ghost" small onClick={async()=>{ if(window.confirm('Delete?')){ await supabase.from('testimonials').delete().eq('id',t.id); fetchResults(); } }} style={{ color:'#c0392b' }}>Delete</Btn>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ENQUIRIES */}
            {resultsSubTab === 'enquiries' && (
              <div>
                <SectionHeader title="Contact Form Enquiries"/>
                {enquiries.length === 0 && <div style={{ color:T.muted, fontSize:13 }}>No enquiries yet.</div>}
                {enquiries.map(e=>(
                  <div key={e.id} style={{ background: e.status==='new'?'#fffbeb':'#fff', border:`1px solid ${e.status==='new'?'#fde68a':'#e8edf5'}`, borderRadius:10, padding:'14px 16px', marginBottom:8 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                      <div>
                        <div style={{ fontWeight:600, color:'#1a2a4a', fontSize:13.5 }}>{e.name}</div>
                        {e.email && <div style={{ fontSize:12, color:T.muted }}>{e.email}</div>}
                        <div style={{ fontSize:13, color:'#374151', marginTop:6, lineHeight:1.6 }}>{e.message}</div>
                        <div style={{ fontSize:11, color:T.muted, marginTop:6 }}>{new Date(e.created_at).toLocaleString()}</div>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {e.status === 'new' && (
                          <Btn variant="ghost" small onClick={async()=>{ await supabase.from('enquiries').update({status:'handled'}).eq('id',e.id); fetchResults(); }}>
                            ✓ Mark Handled
                          </Btn>
                        )}
                        {e.status !== 'new' && <span style={{ fontSize:11, color:'#27ae60' }}>✓ Handled</span>}
                        {e.email && <a href={`mailto:${e.email}`} style={{ fontSize:12, color:'#1a5fa8', textDecoration:'none' }}>Reply by Email</a>}
                        {e.whatsapp && <a href={`https://wa.me/${e.whatsapp}`} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'#25d366', textDecoration:'none' }}>WhatsApp</a>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── REPORTS (FIX 19) ── */}
      {subTab === 'reports' && (() => {
        const now = new Date();
        const RANGES = [['week','This Week'],['month','This Month'],['year','This Year']];

        function inRange(dateStr) {
          if (!dateStr) return false;
          const d = new Date(dateStr);
          if (reportRange === 'week') {
            const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate()-7);
            return d >= weekAgo;
          }
          if (reportRange === 'month') {
            return d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth();
          }
          return d.getFullYear()===now.getFullYear();
        }

        const teacherStats = teachers.map(t => {
          const tBookings = bookings.filter(b => b.teacher_id === t.id && b.status==='confirmed' && inRange(b.created_at));
          const totalBookings = bookings.filter(b => b.teacher_id === t.id && b.status==='confirmed');
          const uniqueStudents = [...new Set(totalBookings.map(b=>b.student_id))].length;
          const earnings = (t.hourly_rate||0) * totalBookings.length;
          const upcoming = bookings.filter(b => b.teacher_id===t.id && b.status==='pending' && new Date(b.created_at)>now).length;
          return { ...t, periodBookings:tBookings.length, totalSessions:totalBookings.length, uniqueStudents, earnings, upcoming };
        });

        function exportCSV() {
          const rows = [
            ['Teacher','Sessions (Period)','Total Sessions','Students','Earnings (AED)','Upcoming'],
            ...teacherStats.map(t => [t.name, t.periodBookings, t.totalSessions, t.uniqueStudents, t.earnings, t.upcoming]),
          ];
          const csv = rows.map(r=>r.join(',')).join('\n');
          const a = document.createElement('a');
          a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
          a.download = `teacher-report-${reportRange}-${now.toISOString().slice(0,10)}.csv`;
          a.click();
        }

        return (
          <div>
            <SectionHeader title="Teacher Performance Report" action={<Btn variant="ghost" small onClick={exportCSV}>⬇ Export CSV</Btn>}/>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {RANGES.map(([k,l])=>(
                <button key={k} onClick={()=>setReportRange(k)}
                  style={{ padding:'6px 14px', borderRadius:6, border:`1px solid ${reportRange===k?T.gold:T.border}`, background:reportRange===k?T.gold+'22':'transparent', color:reportRange===k?T.gold:T.muted, cursor:'pointer', fontSize:12.5 }}>
                  {l}
                </button>
              ))}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                    {['Teacher','Sessions (Period)','Total Sessions','Students','Est. Earnings','Upcoming','Status'].map(h=>(
                      <th key={h} style={{ textAlign:'left', padding:'8px 12px', color:T.muted, fontWeight:600, fontSize:12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teacherStats.map(t=>(
                    <tr key={t.id} style={{ borderBottom:`1px solid ${T.border}` }}>
                      <td style={{ padding:'10px 12px', fontWeight:600, color:'#1a2a4a' }}>{t.name}</td>
                      <td style={{ padding:'10px 12px' }}>{t.periodBookings}</td>
                      <td style={{ padding:'10px 12px' }}>{t.totalSessions}</td>
                      <td style={{ padding:'10px 12px' }}>{t.uniqueStudents}</td>
                      <td style={{ padding:'10px 12px', fontFamily:"'JetBrains Mono',monospace" }}>{t.earnings} AED</td>
                      <td style={{ padding:'10px 12px' }}>{t.upcoming}</td>
                      <td style={{ padding:'10px 12px' }}>
                        <span style={{ fontSize:11, padding:'3px 8px', borderRadius:100, background:t.active?'#e8f5ee':'#f7f9fc', color:t.active?'#1a7a4a':T.muted, border:`1px solid ${t.active?'#c8ead8':T.border}` }}>
                          {t.active?'Active':'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summary stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginTop:24 }}>
              {[
                ['Total Teachers', teachers.length],
                ['Active Teachers', teachers.filter(t=>t.active).length],
                ['Total Students', students.length],
                ['Bookings (Period)', bookings.filter(b=>inRange(b.created_at)).length],
              ].map(([label,val])=>(
                <div key={label} style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:10, padding:'14px', textAlign:'center' }}>
                  <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:26, fontWeight:700, color:'#1a2a4a' }}>{val}</div>
                  <div style={{ fontSize:11, color:T.muted, marginTop:4 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Course Cover Images mini-component ───────────────────────────────────────
function CourseCoverImages() {
  const [courses, setCourses] = useState([]);
  const [urls, setUrls] = useState({});
  const [saving, setSaving] = useState({});
  const [msg, setMsg] = useState('');

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('courses').select('id,name,cover_image_url').order('sort_order');
      setCourses(data || []);
      const u = {};
      (data || []).forEach(c => { u[c.id] = c.cover_image_url || ''; });
      setUrls(u);
    }
    load();
  }, []);

  async function saveCoverUrl(courseId) {
    setSaving(s => ({ ...s, [courseId]:true }));
    await supabase.from('courses').update({ cover_image_url: urls[courseId]||null }).eq('id', courseId);
    setSaving(s => ({ ...s, [courseId]:false }));
    setMsg('✓ Saved');
    setTimeout(() => setMsg(''), 2000);
  }

  return (
    <div>
      {msg && <div style={{ fontSize:12.5, color:'#27ae60', marginBottom:10 }}>{msg}</div>}
      {courses.map(c => (
        <div key={c.id} style={{ display:'flex', gap:10, alignItems:'center', marginBottom:10, padding:'10px 14px', background:'#fff', border:'1px solid #e8edf5', borderRadius:9 }}>
          {urls[c.id] && <img src={urls[c.id]} alt={c.name} style={{ height:48, width:80, objectFit:'cover', borderRadius:6, flexShrink:0 }}/>}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'#1a2a4a', marginBottom:4 }}>{c.name}</div>
            <input value={urls[c.id]||''} onChange={e=>setUrls(u=>({...u,[c.id]:e.target.value}))} placeholder="Cover image URL" style={{ width:'100%', padding:'6px 10px', border:'1px solid #dde3ed', borderRadius:6, fontSize:12.5, outline:'none', boxSizing:'border-box' }}/>
          </div>
          <button onClick={() => saveCoverUrl(c.id)} disabled={saving[c.id]} style={{ padding:'7px 14px', background:'#1a2a4a', color:'#fff', border:'none', borderRadius:7, fontSize:12.5, cursor:'pointer', flexShrink:0 }}>
            {saving[c.id] ? '…' : 'Save'}
          </button>
        </div>
      ))}
      {courses.length === 0 && <div style={{ color:'#9aa3b8', fontSize:13 }}>No courses found. Add courses first.</div>}
    </div>
  );
}
